// SC-N2 — the open-note editor body (sheet header + two-pane layout).
// Presentational rebuild of the N1 editor. EVERY business rule, DB write, gate, id contract,
// and handler is preserved verbatim from N1 — only the DOM assembly changed. Cross-boundary
// plumbing still goes through ctl.onReflect()/ctl.onClose(); helpers via window.HUD.
// Dictation DOM contract preserved: clinical textarea id `vault-notes-<id>`, the mic button and
// the data-speech-lang toggle buttons share one parent (the clinical pane actions), so
// micBtn.parentElement.querySelectorAll('[data-speech-lang]') still finds the toggle.
import { reslotHeaderActions } from './header-slot.js';
import { paragraphsInto, formatNoteTitle, computeNoteChip, isDefaultNoteTitle, noteTimeHM, titleForInput, noteSession, noteSortKey, noteDateLabel, noteSessionType, notePatientCtx } from './helpers.js';
import { bkPut, bkDel, bkGet, bkAll, bkAvailable } from './backup.js';
import { tombAdd, tombHas, tombReopen, tombFlush } from './tombstones.js'; // F-15
import { toggleSessionMenu, paintSessionChip } from './picker.js'; // SC5 L3
import { RichField, docForRow } from './rich-field.js';           // SC6a R3a: the rich clinical field
import { docToPlain, plainToDoc, docHasInlineMarks, docToTidyText, docHasNestedList } from './rich-doc.js';
import { buildRichToolbar } from './rich-toolbar.js'; // SC6a R4
import { startDictation, dictContinuousOK } from './rich-dictation.js';   // SC6a R5
import { runDictationPass, dictBusy, dictAiOn, dictHintDue, dictHintSeen, dictTipDue, dictTipSeen, numfixList, wordfixList, DICT_MAX_CHARS } from './dictation-pass.js'; // SC6c D2/D3; Tidy guards T3
// SC6a R3b: a recovered / stored version → the pair to write: the doc (old-client rule applied) + its mirror.
function _pairOf(content, contentDoc){ const d=docForRow(content, contentDoc).doc; return { content:docToPlain(d), content_doc:d }; }
const H = () => window.HUD || {};

// ── N2b past-sessions floating-window state (MODULE-SCOPED = pure memory; never S/DB/LS) ──
// Survives a refresh()-driven re-render of the sheet (module singleton), so the open
// window + its dragged position aren't disturbed. `_peekCache` holds fetched clinical
// content for the sheet's lifetime only — never written anywhere. All cleared on sheet
// close via resetNotesPeeks() (from index.js _teardown) or when the open note changes.
let _peekId = null;            // the past note shown in the floating window (null = closed)
let _peekPos = null;           // { left, top } px within the sheet once dragged (null = default corner)
let _peekSize = null;          // { w, h } px once resized (null = default size)
let _peekSumOpen = false;      // parent-summary section expanded? (collapsed by default)
let _peekCache = new Map();    // note id → clinical content string (in-memory only)
let _peekOwnerId = null;       // the open note the window belongs to (guards a note switch)
export function resetNotesPeeks(){ _peekId = null; _peekPos = null; _peekSize = null; _peekSumOpen = false; _peekCache = new Map(); _peekOwnerId = null; }

// Tidy guards T3/T5 (decisions 35.10, 36): the same text for "unchanged" — trailing / repeated spaces ignored, line breaks
// count, and a bullet marker "- ", "• " or "* " at the start of a line is the same marker (numbered markers unchanged).
const _tidySame = (a, b) => { const n = s => String(s || '').replace(/[ \t\u00a0]+/g, ' ').replace(/ *\n */g, '\n').trim().replace(/^[-•*] /gm, '• '); return n(a) === n(b); };

// ── Notes SC6a R3a: ONE rich clinical field per open note (MODULE-SCOPED) ──
// Survives every re-render of the same note (Lock/Publish reflect, Tidy, Generate, a remote flag change, a trial start):
// renderEditorBody re-attaches its host element instead of building a new editor, so the undo history is never lost.
// Destroyed on a note switch or sheet teardown — destroy() keeps the last doc + mirror, so a released engine's background
// save (A4) still writes the full text.
let _rich = null; // { id, field }
function _richFieldFor(note){
  if(_rich && _rich.id === note.id && !_rich.field.destroyed) return _rich.field;
  if(_rich) _rich.field.destroy();
  // SC6c D3 (decision 28.11): the natural-dictation hint in the EMPTY editor — only where dictation + the clean-up are on
  // (read at each render, so the settings switch and the UI language apply without a rebuild).
  const hint = () => (!note.locked && H()._hasSpecAiAccess && H()._hasSpecAiAccess() && (window.SpeechRecognition || window.webkitSpeechRecognition) && dictAiOn()) ? H().T('notes_dict_hint') : '';
  _rich = { id: note.id, field: new RichField({ noteId: note.id, placeholder: hint, readOnly: !!note.locked, onForeignTrip: () => _bkForce(note.id) }) };
  return _rich.field;
}
export function resetRichField(){ if(_rich){ _rich.field.destroy(); _rich = null; } }

// Notes SC1 — every vault_notes write goes through here. supabase-js RESOLVES (never throws) on an
// auth/RLS/network failure, so an unchecked `await` treats a failed save as success. Success = no
// error AND exactly one row back (an RLS-filtered write returns 0 rows with no error). `q` is an
// un-awaited insert/upsert/update builder; `.select('id')` makes it return the written row.
async function _writeNote(q, label){
  try{
    const { data, error } = await q.select('id');
    if(error){ console.error('❌ '+label+':', error); return false; }
    if(!data || data.length!==1){ console.error('❌ '+label+': expected 1 row, got '+(data?data.length:0)); return false; }
    return true;
  }catch(e){ console.error('❌ '+label+':', e); return false; }
}

// ══════════════════ Notes SC2 — autosave engine (MODULE-SCOPED) ══════════════════
// One engine for the open note. It survives re-renders of the SAME note (Tidy / Restore / AI summary /
// refresh call renderEditorBody again), which only re-bind the element refs + repaint the pill in place.
// Rules (investigation §9 "SC2 design trap"): the draft maps stay populated for the editor's life; a
// separate `dirty` flag drives saving + the pill; saving never re-renders (no onReflect/re()); a save
// sequence number means an edit made while a save is in flight keeps `dirty` and gets exactly one
// follow-up save; `_trackVaultNoteChange` is registered only after a confirmed write.
const _AS_DEBOUNCE = 2000, _AS_TICK = 30000, _AS_BACKOFF = [2000, 5000, 15000, 30000];
let _as = null;               // the open note's engine (null = none)
let _asInstalled = false;     // window listeners installed once
// A4: engines of notes LEFT without Back (note switch / screen change) whose last change isn't saved yet.
// Memory only: they keep retrying (same backoff + online/visible/focus triggers) until the save lands.
const _asBg = new Set();
function _asBgFlag(){ const S=H().S; if(S) S._notesBgPending = _asBg.size>0; } // read by index.html _appIsDirty

function _asNew(note){
  return { id:note.id, local:!!note.local, dirty:false, editSeq:0, saving:null, queued:false, failing:false,
    stopped:false, released:false, backoffIdx:0, deb:null, tick:null, retry:null, refs:null,
    lastSavedAt:note.lastSaved||null, pill:(note.local?null:'saved'),
    base:null, own:[], conflict:null, // SC3: version token of the loaded row, this tab's own write tokens, conflict state
    ctx:{ childId:note.childId||null, specialistPatientId:note.specialistPatientId||null }, // SC4: stored with a new note's draft
    link:note.appointmentId||null, // SC5: the session link (a new note sends it on its first insert only)
    bkTimer:null, bkQ:Promise.resolve(), bkOkSeq:-1, bkPending:false }; // SC4: on-device draft buffer state
}

// ══════════════════ Notes SC4 — on-device backup of UNSAVED drafts (decision 1; storage in backup.js) ══════════════════
// Written (~500 ms debounce) only while the note is dirty; never for locked or published notes or a note deleted
// elsewhere; deleted once the server confirms a save that includes the latest edits, on discard, on delete and
// when a conflict is resolved. All buffer operations of a note run in order (bkQ), so a late write can never
// re-create an entry that a confirmed save just deleted.
const _AS_BK_DEBOUNCE = 500;
function _bkUser(){ const s=H().session; return s && s.id ? s.id : null; }
function _bkStorable(a){
  if(!a || a.stopped || !a.dirty || !_bkUser()) return false;
  if(a.conflict && a.conflict.kind==='deleted') return false;
  const row=(H().DB.vaultNotes||[]).find(n=>n.id===a.id);
  if(row && (row.locked || row.published)) return false;
  return !!(a.refs && a.refs.ta);
}
function _bkSchedule(a){ clearTimeout(a.bkTimer); a.bkTimer=null; if(_bkStorable(a)) a.bkTimer=setTimeout(()=>{ a.bkTimer=null; _bkWrite(a); },_AS_BK_DEBOUNCE); }
function _bkWrite(a){
  a.bkQ=a.bkQ.then(async()=>{
    if(!_bkStorable(a)) return;
    const seq=a.editSeq, m=_asMineNow(a);
    a.bkPending=true;
    const ok=await bkPut({ userId:_bkUser(), noteId:a.id, isNew:!!a.local, appointmentId:a.local?(a.link||null):null, childId:a.ctx.childId, specialistPatientId:a.ctx.specialistPatientId,
      title:m.title, content:m.content, contentDoc:m.contentDoc, clin:_asClinNow(a), summary:m.summary, base:a.base }); // SC6a: + the doc (capped) + clin (F-19: false for an unchanged plan-read-only field)
    a.bkPending=false;
    if(ok && a.editSeq===seq && a.dirty){ a.bkOkSeq=seq; if(a.pill==='offline') _asSetPill(a,'offline_kept'); }
  }).catch(e=>{ a.bkPending=false; console.error('❌ note draft backup write:', e); });
}
// F-59b-2: the freeze watchdog's device copy for this note. Called synchronously at the trip (the editor still holds its text), same record as _bkWrite but NOT gated on
// `dirty` (the editor is about to be paused). Not for a stopped / deleted / locked / published note. Resolves true when stored.
function _bkForce(id){
  const a=(_as && _as.id===id) ? _as : [..._asBg].find(b=>b.id===id);
  if(!a || a.stopped || !_bkUser() || !(a.refs && a.refs.ta)) return Promise.resolve(false);
  if(a.conflict && a.conflict.kind==='deleted') return Promise.resolve(false);
  const row=(H().DB.vaultNotes||[]).find(n=>n.id===id);
  if(row && (row.locked || row.published)) return Promise.resolve(false);
  const m=_asMineNow(a), clin=_asClinNow(a); // read NOW, before the pause
  a.bkQ=a.bkQ.then(async()=>{
    a.bkPending=true;
    const ok=await bkPut({ userId:_bkUser(), noteId:a.id, isNew:!!a.local, appointmentId:a.local?(a.link||null):null, childId:a.ctx.childId, specialistPatientId:a.ctx.specialistPatientId,
      title:m.title, content:m.content, contentDoc:m.contentDoc, clin, summary:m.summary, base:a.base });
    a.bkPending=false;
    if(!ok) console.error('❌ note freeze-watchdog device copy: not stored');
    return ok;
  }).catch(e=>{ a.bkPending=false; console.error('❌ note freeze-watchdog device copy:', e); return false; });
  return a.bkQ;
}
function _bkClear(a){
  if(!a) return; clearTimeout(a.bkTimer); a.bkTimer=null; a.bkOkSeq=-1;
  const uid=_bkUser(); if(!uid) return;
  a.bkQ=a.bkQ.then(()=>bkDel(uid,a.id)).catch(e=>console.error('❌ note draft backup delete:', e));
}
// F-5: the rich doc behind a note's unsaved draft, while its engine still holds it (open, or waiting in the background
// queue after a switch / screen change). Reopening the note loads this doc rather than a plain-text rebuild of the draft
// map, so a later save never writes a formatting-free content_doc over the note.
function _asDraftDoc(id){
  const a=(_as && _as.id===id) ? _as : [..._asBg].find(b=>b.id===id);
  const t=a && a.dirty && a.refs && a.refs.ta;
  return (t && t.isRich && t.doc && t.doc.type==='doc') ? t.doc : null;
}
// The latest edits are safely on the device (a reload/close loses nothing) → no leave-page prompt needed.
function _bkSafe(a){ return !!(a && bkAvailable() && a.bkOkSeq===a.editSeq && !a.bkPending && !a.bkTimer); }
function _asOffPill(a){ return _bkSafe(a) ? 'offline_kept' : 'offline'; }

// ── SC4 B2: recovery ──
// Opening a note that has a device copy. `r` = the server version just loaded, or null when the load failed
// (`err`). Returns true when the device copy was put into the editor (the caller then enables it).
async function _bkRecoverOnOpen(id, r, err){
  const a=_as, uid=_bkUser(); if(!a || a.id!==id || !uid || a.dirty) return false;
  // Only on this editor session's FIRST load: a device copy found later (a re-render after Lock/Publish, a refresh)
  // is this session's own, already being saved/cleared — never re-apply it.
  if(a.bkChecked || a.editSeq>0) return false; a.bkChecked=true;
  await a.bkQ; // this note's queued device-copy writes/deletes land first
  if(_pendingMine.has(id)){ // SC5: text from a note whose session was already taken → SC3 banner, mine = that text
    const pm=_pendingMine.get(id); _pendingMine.delete(id);
    _asShow(a,pm); a.editSeq++; a.dirty=true; _asMirror(a); if(r) a.base=r.last_saved;
    _asConflict(a,'conflict',r?r.last_saved:null); _bkSchedule(a); return true;
  }
  const ent=await bkGet(uid,id); if(!ent || a!==_as || a.dirty) return false;
  const mine={ title:ent.title, content:ent.content, contentDoc:ent.contentDoc||null, summary:ent.summary }; // SC6a: + the doc
  const take=()=>{ _asShow(a,mine); a.editSeq++; a.dirty=true; a.bkOkSeq=a.editSeq; _asMirror(a); }; // the shown text IS the device copy
  if(r){
    if(ent.base && _tokEq(ent.base, r.last_saved)){ take(); a.base=r.last_saved; _asSave(a); return true; }     // nobody saved since → save it
    take(); a.base=r.last_saved; _asConflict(a,'conflict',r.last_saved); return true;                            // moved on → SC3 banner, mine = device copy
  }
  const noRow = err && (err.code==='PGRST116' || /0 rows|no rows/i.test(err.message||''));
  if(noRow && !ent.isNew){ take(); _asConflict(a,'deleted'); return true; }                                   // deleted on the server → red banner
  take(); a.base=ent.base||null; a.local=!!ent.isNew; return true; // offline (or a never-created note) → SC2 path: the caller enables the editor, then _asContentReady() saves it
}
// A device copy that can't be shown in the list yet (a new note not yet created, or a note deleted on the
// server): list a placeholder row so the user can open it and see it.
function _bkGhost(ent, kind){
  const DB=H().DB, ns=DB.vaultNotes||[];
  if(ns.some(n=>n.id===ent.noteId)) return;
  const ms=Number(String(ent.noteId).replace(/^v_/,''));
  ns.unshift({ id:ent.noteId, specialistId:ent.userId, childId:ent.childId, specialistPatientId:ent.specialistPatientId,
    date:window.HUD._ymd(new Date(ms||Date.now())), title:ent.title||'', content:'', summary:ent.summary||'', published:false, locked:false,
    isDraft:true, lastSaved:'', local:!!ent.isNew, bkGhost:kind||'pending' });
  DB.vaultNotes=ns;
}
// App start (after the specialist notes list loads) and every `online`: this user's device copies of notes
// that aren't open. Unchanged on the server → saved now (conditional / insert), entry deleted, ONE toast.
// Moved on → kept (banner when opened). Deleted on the server → kept + listed (red banner when opened).
// Offline / not reachable → left as they are.
let _rcRunning=false, _rcInstalled=false;
export async function notesRecover(){
  if(!_rcInstalled){ _rcInstalled=true; window.addEventListener('online', ()=>{ notesRecover(); }); }
  if(_rcRunning) return; _rcRunning=true;
  try{
    const uid=_bkUser(); if(!uid) return;
    await tombFlush(uid, H()._supa); // F-15: deletes owed by this device (offline deletes, ids that came back)
    const entries=await bkAll(uid); if(!entries.length) return;
    const { _supa } = H(); let saved=0;
    for(const ent of entries){
      if((_as && _as.id===ent.noteId) || [..._asBg].some(b=>b.id===ent.noteId)) continue; // an open/queued editor owns it
      if(tombHas(uid, ent.noteId)){ await bkDel(uid, ent.noteId); continue; } // F-15: deleted on this device → never re-created
      if(navigator.onLine===false){ if(ent.isNew) _bkGhost(ent); continue; }
      let row=null, err=null;
      try{ const { data, error } = await _supa.from('vault_notes').select('last_saved,title').eq('id',ent.noteId); if(error) err=error; else row=data&&data[0]; }
      catch(e){ err=e; }
      if(err){ if(ent.isNew) _bkGhost(ent); continue; }
      const DB=H().DB;
      if(!row && ent.isNew){ // never created → create it now, with its stored patient context (+ session link)
        const ts=new Date().toISOString();
        const ins=(link)=>_supa.from('vault_notes').insert(Object.assign({ id:ent.noteId, specialist_id:uid,
          title:ent.title||formatNoteTitle(new Date(Number(String(ent.noteId).replace(/^v_/,''))||Date.now())),
          summary:(ent.summary||'').trim()?ent.summary:null, is_draft:true, locked:false, published:false, last_saved:ts, appointment_id:link },
          ent.clin===false ? {} : _pairOf(ent.content, ent.contentDoc), // SC6a: content + content_doc together (a free-tier copy: neither)
          H()._noteFkPayload({ childId:ent.childId, specialistPatientId:ent.specialistPatientId }))).select('last_saved');
        let { data, error } = await ins(ent.appointmentId||null);
        // SC5 (decision 22): the session already has a note (23505) or the link is refused (42501) → save the text as
        // a GENERAL note instead — the text is never lost.
        if(error && ent.appointmentId && (error.code==='23505'||error.code==='42501') && !/vault_notes_pkey/.test((error.message||'')+(error.details||''))){
          ({ data, error } = await ins(null)); ent.appointmentId=null;
        }
        if(error || !data || data.length!==1){ if(error) console.error('❌ recover new note:', error); _bkGhost(ent); continue; }
        // F-15 (path E): the user deleted this note WHILE this insert was in flight → delete the row again, never list it
        if(tombHas(uid, ent.noteId)){ await bkDel(uid,ent.noteId); tombReopen(uid, ent.noteId); await tombFlush(uid, _supa); continue; }
        await bkDel(uid,ent.noteId); saved++;
        const ns=DB.vaultNotes.filter(n=>n.id!==ent.noteId);
        ns.unshift({ id:ent.noteId, specialistId:uid, childId:ent.childId, specialistPatientId:ent.specialistPatientId,
          date:window.HUD._ymd(new Date(Number(String(ent.noteId).replace(/^v_/,''))||Date.now())), title:ent.title||'', content:'',
          summary:ent.summary||'', published:false, locked:false, isDraft:true, lastSaved:data[0].last_saved||ts,
          writtenAt:new Date(Number(String(ent.noteId).replace(/^v_/,''))||Date.now()).toISOString(), appointmentId:ent.appointmentId||null });
        DB.vaultNotes=ns; continue;
      }
      if(!row || row.title==='__DELETED__'){ _bkGhost(ent,'deleted'); continue; } // keep until the user has seen it
      if(ent.base && _tokEq(row.last_saved, ent.base)){ // nobody saved since → save it (conditional)
        const ts=new Date().toISOString();
        const upd=Object.assign(ent.clin===false ? {} : _pairOf(ent.content, ent.contentDoc), { summary:(ent.summary||'').trim()?ent.summary:null, last_saved:ts }, ent.title?{ title:ent.title }:{}); // '' = the default title → keep the stored one; SC6a: both columns (a free-tier copy: neither)
        const { data, error } = await _supa.from('vault_notes').update(upd)
          .eq('id',ent.noteId).eq('last_saved',ent.base).select('last_saved');
        if(error){ console.error('❌ recover note:', error); continue; }
        if(data && data.length===1){
          await bkDel(uid,ent.noteId); saved++;
          const ns=DB.vaultNotes, i=ns.findIndex(n=>n.id===ent.noteId);
          if(i>-1){ if(ent.title) ns[i].title=ent.title; ns[i].summary=ent.summary||''; ns[i].lastSaved=data[0].last_saved||ts; DB.vaultNotes=ns; }
        }
        continue;
      }
      // the server moved on → keep it; opening the note shows the SC3 banner with mine = the device copy
    }
    if(saved) H().toast(H().T('notes_recovered_saved'));
  }catch(e){ console.error('❌ recover note drafts:', e); }
  finally{ _rcRunning=false; }
}

// ══════════════════ Notes SC3 — conflict detection (optimistic concurrency on last_saved) ══════════════════
// `last_saved` is an opaque version token (no new column). Every write to an existing note is conditional on
// the token this editor loaded (`base`); 0 rows back means the row changed or vanished elsewhere. Nothing is
// ever overwritten without a choice: on a conflict autosave pauses (no writes at all) until the user decides.
function _tokEq(x,y){ if(x==null||y==null) return false; if(x===y) return true; const a=Date.parse(x), b=Date.parse(y); return !isNaN(a) && a===b; }
function _asOwnTok(a,ts){ a.own.push(ts); if(a.own.length>200) a.own.shift(); }   // echo suppression by TOKEN
function _asIsOwn(a,tok){ return a.own.some(t=>_tokEq(t,tok)); }
function _asClearDrafts(id){ const S=H().S; if(!S) return; if(S._vaultDraftContent) delete S._vaultDraftContent[id]; if(S._vaultDraftTitle) delete S._vaultDraftTitle[id]; if(S._vaultTidyRaw) delete S._vaultTidyRaw[id]; }
// 0 rows and no error: find out why (follow-up read of the version token).
async function _asClassify(a,label){
  try{
    const { data, error } = await H()._supa.from('vault_notes').select('last_saved,title').eq('id',a.id);
    if(error){ console.error('❌ '+label+' (version check):', error); return { r:'fail' }; }
    const row=data&&data[0];
    if(!row || row.title==='__DELETED__') return { r:'deleted' };
    if(_tokEq(row.last_saved,a.base)){ console.error('❌ '+label+': 0 rows but the version is unchanged'); return { r:'fail' }; }
    return { r:'conflict', other:row.last_saved };
  }catch(e){ console.error('❌ '+label+' (version check):', e); return { r:'fail' }; }
}
// Conditional UPDATE of an existing note: → {r:'ok',ts} | {r:'fail'} (SC2 retry path) | {r:'conflict',other} | {r:'deleted'}.
async function _asCondWrite(a, fields, label){
  const ts=new Date().toISOString();
  _asOwnTok(a,ts); // before sending: the realtime echo can beat the HTTP response
  let data, error;
  try{ ({ data, error } = await H()._supa.from('vault_notes').update(Object.assign({}, fields, { last_saved:ts })).eq('id',a.id).eq('last_saved',a.base).select('last_saved')); }
  catch(e){ console.error('❌ '+label+':', e); return { r:'fail' }; }
  if(error){ console.error('❌ '+label+':', error); return { r:'fail', code:error.code, msg:(error.message||'')+' '+(error.details||'') }; }
  if(data && data.length===1){ a.base=data[0].last_saved||ts; _asOwnTok(a,a.base); return { r:'ok', ts }; }
  if(data && data.length>1){ console.error('❌ '+label+': expected 1 row, got '+data.length); return { r:'fail' }; }
  return _asClassify(a,label);
}
// Enter the conflict / deleted-elsewhere state: no writes at all (open note or background queue).
function _asConflict(a, kind, other){
  if(!a) return;
  a.conflict={ kind, other:other||null };
  _asClearTimers(a); a.failing=false; a.queued=false;
  if(a.released){
    if(kind==='deleted'){ _asBg.delete(a); a.stopped=true; a.dirty=false; _asClearDrafts(a.id); } // drop: never re-create it
    // a conflict stays queued (text in memory, no retries); reopening the note shows the banner
    _asBgFlag();
  }
  _asSetPill(a, kind==='conflict' ? 'conflict' : null);
  _asMirror(a); _asPaintConflict(a);
}
function _asPaintConflict(a){ const r=a&&a.refs; if(r&&r.onConflictUI){ try{ r.onConflictUI(a.conflict); }catch(e){ console.error('❌ conflict UI:', e); } } }
// Realtime (index.html vault_notes channel → HUD_NOTES.onRealtime): a change to an open/queued note that is
// neither the loaded version nor one of this tab's own writes is a conflict NOW; a DELETE is deleted-elsewhere.
// Writes that don't change last_saved (homework flag, parent read receipt, patient merge) never conflict.
// F-15: rows from the server (the list refresh) minus notes deleted on this device; a tombstoned id that came back is
// deleted again (best effort, online).
export function notesTombFilter(rows){
  const uid=_bkUser(); if(!uid || !Array.isArray(rows)) return rows;
  const back=rows.filter(r=>r && tombHas(uid, r.id));
  if(!back.length) return rows;
  back.forEach(r=>tombReopen(uid, r.id)); tombFlush(uid, H()._supa);
  return rows.filter(r=>!(r && tombHas(uid, r.id)));
}
export function notesTombFlush(){ const uid=_bkUser(); return uid ? tombFlush(uid, H()._supa) : Promise.resolve(0); }
export function notesOnRealtime(p){
  const id=(p&&p.new&&p.new.id)||(p&&p.old&&p.old.id); if(!id) return;
  // F-15: a note deleted on this device that shows up again (a late first save, another path) → deleted again
  if(p.eventType!=='DELETE'){ const uid=_bkUser(); if(uid && tombHas(uid, id)){ tombReopen(uid, id); tombFlush(uid, H()._supa); return; } }
  for(const a of [_as, ..._asBg]){
    if(!a || a.id!==id || a.stopped || a.conflict) continue;
    if(p.eventType==='DELETE'){ _asConflict(a,'deleted'); continue; }
    const n=p.new||{}; if(!('last_saved' in n)) continue;
    const tok=n.last_saved;
    if(_asIsOwn(a,tok)) continue;                           // our own echo
    if(n.title==='__DELETED__'){ _asConflict(a,'deleted'); continue; }
    if(a.local || a.base==null || _tokEq(tok,a.base)) continue; // not loaded yet / version unchanged
    _asOnNewVersion(a,tok); // C4 (decision 18): idle → load it in place; unsaved edits → the banner
  }
}
// ── C2: the user's choices (decision 8 — nothing is overwritten without one) ──
function _asMineNow(a){ const r=a.refs||{}; return { title:r.ti?r.ti.value:'', content:r.ta?r.ta.value:'', contentDoc:(r.ta&&r.ta.isRich)?r.ta.doc:null, summary:r.sta?r.sta.value:'' }; } // SC6a: + the doc
function _asShow(a, v){ // put a version into the editor fields + the draft maps, WITHOUT an input event (no autosave)
  const r=a.refs||{}, S=H().S, tIn=titleForInput({ title:v.title }); // SC5: a default title shows as '' (the placeholder = the date)
  let clin=v.content||'';
  if(r.ta && r.ta.isRich){ const d=docForRow(v.content, v.contentDoc).doc; r.ta.setDoc(d,{ undoable:true }); clin=docToPlain(d); } // SC6a: the doc (old-client rule)
  else if(r.ta) r.ta.value=clin;
  if(r.ti) r.ti.value=tIn; if(r.sta) r.sta.value=v.summary||'';
  if(!S._vaultDraftContent) S._vaultDraftContent={}; S._vaultDraftContent[a.id]=clin;
  if(!S._vaultDraftTitle) S._vaultDraftTitle={}; S._vaultDraftTitle[a.id]=tIn;
  if(a===_as) S.vaultSummary=v.summary||'';
  const DB=H().DB, ns=DB.vaultNotes, i=ns.findIndex(n=>n.id===a.id);
  if(i>-1){ if(v.title) ns[i].title=v.title; ns[i].summary=v.summary||null; DB.vaultNotes=ns; }
}
// SC5 L2: the title a save writes. Untouched field → the stored title as is (stored titles are never rewritten).
// Edited: a custom title, or — emptied — the note's default title (the session's date/time, else its creation time).
function _asTitleNow(a, row){
  const r=a.refs||{}, S=H().S;
  const edited=!!(S._vaultDraftTitle && S._vaultDraftTitle[a.id]!=null);
  const v=(r.ti ? r.ti.value : '');
  if(!edited) return row && row.title || v || _asDefaultTitle(a,row);
  if(v.trim()) return v;
  return (row && isDefaultNoteTitle(row) && row.title) ? row.title : _asDefaultTitle(a,row);
}
function _asDefaultTitle(a,row){
  const s=noteSession(row||{ appointmentId:a.link });
  if(s){ const m=String(s.time||'').match(/(\d{1,2}):(\d{2})/); const d=new Date(String(s.date)+'T'+(m?m[1].padStart(2,'0')+':'+m[2]:'12:00')+':00'); if(!isNaN(d)) return formatNoteTitle(d); }
  const ms=Number(String(a.id).replace(/^v_/,''));
  return formatNoteTitle(new Date(ms||Date.now()));
}
// Keep my version: adopt the server's CURRENT version as base, then save mine with the normal conditional
// update. If it changed again in between, that save hits a new conflict → the banner shows again.
async function _asKeepMine(a){
  if(!a||!a.conflict||a.conflict.kind!=='conflict') return;
  let row;
  try{ const { data, error } = await H()._supa.from('vault_notes').select('last_saved,title').eq('id',a.id);
    if(error){ console.error('❌ keep my version (version read):', error); if(a.refs&&a.refs.showErr) a.refs.showErr('notes_err_save',()=>_asKeepMine(a)); return; }
    row=data&&data[0];
  }catch(e){ console.error('❌ keep my version (version read):', e); if(a.refs&&a.refs.showErr) a.refs.showErr('notes_err_save',()=>_asKeepMine(a)); return; }
  if(!row||row.title==='__DELETED__'){ a.conflict=null; _asConflict(a,'deleted'); return; }
  a.base=row.last_saved; a.conflict=null; a.dirty=true; a.editSeq++; a.backoffIdx=0;
  _asPaintConflict(a); _asMirror(a);
  return _asSave(a);
}
// Use the other version: load it into the editor (base = its version); keep MY version in memory with a
// Restore chip. Nothing to save until an edit.
async function _asUseOther(a){
  if(!a||!a.conflict||a.conflict.kind!=='conflict') return;
  const r=await H()._fetchVaultNoteContent(a.id);
  if(r.error){ if(a.refs&&a.refs.showErr) a.refs.showErr('notes_err_load',()=>_asUseOther(a)); return; }
  a.mine=_asMineNow(a);
  _asShow(a,{ title:r.title, content:r.content, contentDoc:r.content_doc||null, summary:r.summary }); // SC6a: + the doc
  a.base=r.last_saved; a.lastSavedAt=r.last_saved; a.conflict=null; a.dirty=false; a.failing=false; _asClearTimers(a);
  _bkClear(a); // SC4: conflict resolved → the device copy goes (Restore re-creates it on the next edit)
  _asSetPill(a,'saved'); _asMirror(a); _asPaintConflict(a);
}
// Restore my version: put it back and mark it dirty → autosave saves it (conditionally, on the new base).
function _asRestoreMine(a){
  if(!a||!a.mine) return;
  const m=a.mine; a.mine=null; a.restoring=true;
  _asShow(a,m); a.restoring=false;
  _asPaintConflict(a);
  _asEdited();
}
// Copy my text: title + clinical + summary as plain text (clipboard; fallback: select it for a manual copy).
async function _asCopyMine(a){
  const m=_asMineNow(a); const text=[m.title,m.content,m.summary].map(s=>(s||'').trim()).filter(Boolean).join('\n\n');
  try{ await navigator.clipboard.writeText(text); return true; }
  catch(_){
    try{ const t=document.createElement('textarea'); t.value=text; t.setAttribute('readonly',''); t.style.position='fixed'; t.style.opacity='0';
      document.body.appendChild(t); t.select(); const ok=document.execCommand && document.execCommand('copy'); if(ok) t.remove(); else setTimeout(()=>t.remove(),10000); return !!ok; }
    catch(e){ console.error('❌ copy my text:', e); return false; }
  }
}
// ── SC5 L2: the session link ──
// The row's link (+ its session from memory) in the list / header.
function _asSetRowLink(a, apptId, sess){
  const DB=H().DB, ns=DB.vaultNotes, i=ns.findIndex(n=>n.id===a.id);
  if(i>-1){ ns[i].appointmentId=apptId||null; ns[i].session=(apptId&&sess)||null; DB.vaultNotes=ns; } // L3: the picker passes the session it fetched
  const r=a.refs; if(r && r.paintLink){ try{ r.paintLink(); }catch(e){ console.error('❌ session link repaint:', e); } }
}
// A note's text put aside for another note id (the "session taken" case): shown there as SC3's "mine".
const _pendingMine=new Map();
const _NOTE_ROWCOLS='id,child_id,specialist_patient_id,title,summary,is_draft,locked,published,pending_publish,last_saved,specialist_id,created_at,parent_read_at,homework_prompt_shown,appointment_id';
function _mapNoteRow(n){
  return { id:n.id, childId:n.child_id, specialistPatientId:n.specialist_patient_id||null, title:n.title||'', content:'', summary:n.summary||'',
    isDraft:n.is_draft!==false, locked:!!n.locked, published:!!n.published, pendingPublish:!!n.pending_publish, lastSaved:n.last_saved||'',
    specialistId:n.specialist_id, date:H()._localDateOf(n.created_at), parentReadAt:n.parent_read_at||null,
    homeworkPromptShown:!!n.homework_prompt_shown, writtenAt:n.created_at||null, appointmentId:n.appointment_id||null, session:null };
}
// Another device already made a note for this session (23505 on the one-per-session index): open THAT note, with
// this note's text as SC3's "mine" (banner: Keep mine / Use the other version / Copy). Never a second note.
async function _asSessionTaken(a){
  const apptId=a.link, mine=_asMineNow(a);
  let row=null;
  try{ const { data, error } = await H()._supa.from('vault_notes').select(_NOTE_ROWCOLS).eq('appointment_id',apptId).neq('title','__DELETED__');
    if(error) console.error('❌ session note lookup:', error); else row=data&&data[0]; }catch(e){ console.error('❌ session note lookup:', e); }
  if(!row){ a.failing=true; _asSetPill(a,'retrying'); _asFailed(a); return; } // not visible yet → retry later (the text stays)
  _pendingMine.set(row.id, mine);
  a.stopped=true; a.dirty=false; _asClearTimers(a); _asMirror(a); _bkClear(a);
  const DB=H().DB; DB.vaultNotes=[_mapNoteRow(row), ...(DB.vaultNotes||[]).filter(n=>n.id!==a.id && n.id!==row.id)];
  _asClearDrafts(a.id);
  try{ H().toast(H().T('notes_session_taken')); }catch(_){}
  const r=a.refs; if(r && r.openNote) r.openNote(row.id);
}
// Move the open note to a session (or to none). A never-saved note only changes its pending link (nothing is
// written until it has content). A saved note: the SC3 conditional update of appointment_id (+ last_saved).
// → {r:'ok'} | {r:'fail', code} (42501 locked / refused, 23505 that session already has a note) | conflict / deleted.
export async function notesMoveTo(apptId, sess){
  const a=_as; if(!a || a.stopped) return { r:'fail' };
  if(a.saving) await a.saving; // L3: a first insert in flight carries the old link → wait, then move the saved row
  if(a!==_as || a.stopped) return { r:'fail' };
  if(a.local){ a.link=apptId||null; _asSetRowLink(a, a.link, sess); if(a.dirty) _bkSchedule(a); return { r:'ok' }; }
  if(a.conflict || a.base==null) return { r:'fail' };
  const res=await _asCondWrite(a,{ appointment_id:apptId||null },'move note');
  if(res.r==='ok'){ a.link=apptId||null; H()._trackVaultNoteChange(a.id); a.lastSavedAt=a.base; _asSetRowLink(a, apptId||null, sess);
    const DB=H().DB, ns=DB.vaultNotes, i=ns.findIndex(n=>n.id===a.id); if(i>-1){ ns[i].lastSaved=a.base; DB.vaultNotes=ns; } }
  else if(res.r==='conflict'){ _asOnNewVersion(a,res.other); }
  else if(res.r==='deleted'){ _asConflict(a,'deleted'); }
  return res;
}
// A new, not-yet-saved note (A3) — for a patient, optionally for a session (L3/L4 use this). Nothing is written
// until it has text; its default title is the session's date/time, else now.
export function notesCreateLocal({ childId=null, specialistPatientId=null, appointment=null }={}){
  const { DB, S, session } = H();
  const now=new Date();
  let when=now;
  if(appointment){ const m=String(appointment.time||'').match(/(\d{1,2}):(\d{2})/); const d=new Date(String(appointment.date)+'T'+(m?m[1].padStart(2,'0')+':'+m[2]:'12:00')+':00'); if(!isNaN(d)) when=d; }
  const nn={ id:'v_'+Date.now(), specialistId:session?.specialistId||null, childId, specialistPatientId,
    date:window.HUD._ymd(now), title:formatNoteTitle(when), content:'', published:false, locked:false, isDraft:true, summary:'', lastSaved:'',
    writtenAt:now.toISOString(), appointmentId:appointment?appointment.id:null,
    session:appointment?{ id:appointment.id, date:appointment.date, time:appointment.time, title:appointment.title||'', deleted_at:null }:null, // L3: shown before any refresh
    local:true }; // Notes SC2 A3 (QA-24): NO row yet — the first autosave with real text creates it
  if(!S._vaultDraftContent) S._vaultDraftContent={}; S._vaultDraftContent[nn.id]=''; // known-empty: never LOAD a row that doesn't exist
  const ns=DB.vaultNotes; ns.unshift(nn); DB.vaultNotes=ns;
  return nn;
}
// ── C4 (decision 18, refines 8): live update when idle ──
// Another device saved. This window has NO unsaved edits → load the new version IN PLACE silently (text,
// title, summary, lock/publish/pending flags, base), keeping caret + scroll. Unsaved edits that the other
// version doesn't include → the conflict banner. Unsaved edits identical to it → adopt it, nothing to do.
const _AS_ROWCOLS='content,content_doc,title,summary,last_saved,locked,published,pending_publish,is_draft,appointment_id'; // SC6a: + content_doc
function _asSetKeep(el, v){ // set a field's value, keeping caret + scroll as close as possible
  if(!el) return; v=v||''; if(el.value===v) return;
  const focused=document.activeElement===el; const s=el.selectionStart, en=el.selectionEnd, top=el.scrollTop;
  el.value=v;
  if(focused && s!=null){ try{ el.setSelectionRange(Math.min(s,v.length), Math.min(en,v.length)); }catch(_){} }
  el.scrollTop=top;
}
async function _asOnNewVersion(a, tok){
  if(!a || a.stopped) return;
  if(a.released){ _asConflict(a,'conflict',tok); return; }  // not open: keep my queued text, ask on reopen
  if(a.checking){ a.recheck=tok; return; }                   // one check at a time; re-run with the newest token
  a.checking=true;
  let row, err=false;
  try{ const { data, error } = await H()._supa.from('vault_notes').select(_AS_ROWCOLS).eq('id',a.id);
    if(error){ console.error('❌ load newer version:', error); err=true; } else row=data&&data[0]; }
  catch(e){ console.error('❌ load newer version:', e); err=true; }
  a.checking=false;
  if(a.stopped || a!==_as){ return; }
  if(err){ if(a.dirty) _asConflict(a,'conflict',tok); a.recheck=null; return; } // can't compare → never overwrite: ask (only if I have edits)
  if(!row || row.title==='__DELETED__'){ _asConflict(a,'deleted'); a.recheck=null; return; }
  if(_tokEq(row.last_saved,a.base)){ if(a.recheck){ const t=a.recheck; a.recheck=null; return _asOnNewVersion(a,t); } return; } // nothing new after all
  const mine=_asMineNow(a);
  const same=(x,y)=>(x||'')===(y||'');
  if(!a.dirty && !a.saving){ _asApplyRemote(a,row); }
  else if(same(mine.content,docToPlain(docForRow(row.content,row.content_doc).doc)) && same(mine.title,titleForInput({ title:row.title })) && same((mine.summary||'').trim()?mine.summary:'', row.summary||'')){ // SC6a: compare mirrors
    a.base=row.last_saved; a.lastSavedAt=row.last_saved; a.dirty=false; a.failing=false; _asSetPill(a,'saved'); _asMirror(a); // identical → adopt
  }
  else _asConflict(a,'conflict',row.last_saved);
  if(a.recheck && !a.conflict){ const t=a.recheck; a.recheck=null; return _asOnNewVersion(a,t); }
  a.recheck=null;
  // still unsaved and no conflict (e.g. nothing new after all) → the held save runs again
  if(a.dirty && !a.conflict && !a.stopped){ clearTimeout(a.deb); a.deb=setTimeout(()=>{ a.deb=null; _asSave(a); },_AS_DEBOUNCE); }
}
function _asApplyRemote(a, row){
  const r=a.refs||{}, S=H().S, DB=H().DB;
  _asSetKeep(r.ti,titleForInput({ title:row.title })); _asSetKeep(r.sta,row.summary);
  // SC6a: the clinical field gets the remote DOC (old-client rule: a stale doc → content wins) — replaced with NO history
  // step and the history reset (undo can't reach into the old version), the selection clamped, the scroll kept.
  let _clin=row.content||'';
  if(r.ta && r.ta.isRich){ const d=docForRow(row.content,row.content_doc).doc; _clin=docToPlain(d); if(_clin!==r.ta.value || JSON.stringify(d)!==JSON.stringify(r.ta.doc)) r.ta.setDoc(d); }
  else _asSetKeep(r.ta,row.content);
  if(!S._vaultDraftContent) S._vaultDraftContent={}; if(S._vaultDraftContent[a.id]!=null) S._vaultDraftContent[a.id]=_clin;
  if(S._vaultDraftTitle && S._vaultDraftTitle[a.id]!=null) S._vaultDraftTitle[a.id]=titleForInput({ title:row.title });
  if(a===_as && S.vaultSummary!=null) S.vaultSummary=row.summary||'';
  const ns=DB.vaultNotes, i=ns.findIndex(n=>n.id===a.id); let flags=false, linkChanged=false;
  if(i>-1){
    const n=ns[i];
    flags = !!n.locked!==!!row.locked || !!n.published!==!!row.published || !!n.pendingPublish!==!!row.pending_publish;
    n.title=row.title||''; n.summary=row.summary||''; n.locked=!!row.locked; n.published=!!row.published; n.pendingPublish=!!row.pending_publish;
    n.isDraft=row.is_draft!==false; n.lastSaved=row.last_saved;
    if('appointment_id' in row && (n.appointmentId||null)!==(row.appointment_id||null)){ n.appointmentId=row.appointment_id||null; n.session=null; linkChanged=true; } // SC5: moved elsewhere → chip/list follow (session from memory)
    DB.vaultNotes=ns;
  }
  a.base=row.last_saved; a.lastSavedAt=row.last_saved; a.failing=false;
  if(S._vaultTidyRaw) delete S._vaultTidyRaw[a.id]; // F-53: a remote version replaced the text → no tidy stash outlives it (was in refresh())
  _asSetPill(a,'saved'); _asMirror(a);
  // F-53: lock / publish / pending changed → painted IN PLACE (the live editor keeps its element, focus and caret). The
  // editor compares with what it SHOWS (a list refresh may already have put the new flags in DB.vaultNotes). Only a change
  // it can't paint (a flag turned off) still re-renders, as before.
  let painted=true;
  if(r.paintFlags){ try{ painted=r.paintFlags(); }catch(e){ painted=false; console.error('❌ live update repaint:', e); } }
  else painted=!flags;
  if(!painted && r.reflect){ if(S._vaultDraftContent) S._vaultDraftContent[a.id]=_clin; try{ r.reflect(); }catch(e){ console.error('❌ live update re-render:', e); } return; }
  if(linkChanged && r.paintLink){ try{ r.paintLink(); }catch(e){ console.error('❌ session link repaint:', e); } } // SC5: in place
}
// F-53: the list refresh saw a version of the OPEN note that this editor doesn't have (a live update it missed: sleep,
// reconnect) → C4, exactly as if it had arrived live (in place when there are no unsaved edits, else the banner). Never a
// redraw. Updates about other notes only repaint the past-sessions list.
// F-53: the open note is missing from the list while it has unsaved text → ask the server with C4's one-row read: gone or
// soft-deleted → SC3's deleted-elsewhere banner (read-only, Copy my text); still there → nothing changes.
export function notesCheckGone(id){
  const a=_as; if(!a || a.id!==id || a.stopped || a.conflict || a.local || a.base==null) return;
  _asOnNewVersion(a, a.base);
}
export function notesCatchUp(id, tok){
  const a=_as; if(!a || a.id!==id) return;
  if(!a.stopped && !a.conflict && !a.local && a.base!=null && tok && !_tokEq(tok,a.base) && !_asIsOwn(a,tok)) _asOnNewVersion(a,tok);
  const r=a.refs; if(r && r.paintPast){ try{ r.paintPast(); }catch(e){ console.error('❌ past sessions repaint:', e); } }
}
// The content load (SC1 loader, now also returning last_saved) sets the version this editor is based on.
function _asLoaded(id, r){ const a=_as; if(a && a.id===id && !a.conflict && r && r.last_saved!==undefined) a.base=r.last_saved; }
// F-19 (QR-a A): a clinical field that is read-only only because there's no plan (field.planRO) writes the clinical
// columns ONLY when its doc differs from the last loaded / saved one — an edit made before the lapse, a recovered
// device copy — so a summary edit or Publish there writes NEITHER column (as the free tier always did). An unknown
// baseline errs on writing them (text is never lost).
function _asClinBase(id, field){ const a=_as; if(a && a.id===id && field && field.isRich) a.clinBase=JSON.stringify(field.doc); }
function _clinUnchangedRO(a, t){ return !!(t && t.planRO && a && a.clinBase!=null && JSON.stringify(t.doc)===a.clinBase); }
function _asClinNow(a){ const t=a && a.refs && a.refs.ta; return !!(t && t.isRich) && !_clinUnchangedRO(a, t); }
function _asMirror(a){ if(a===_as){ const S=H().S; if(S) S.vaultHasUnsaved = !!(a.dirty || a.saving); } }
// U-2 (5b): note ids whose content hasn't loaded in the open editor yet. Their pill never claims "Saved <time>" — a saved
// time is only true for text that is on screen (Eytan's walkthrough: "Couldn't load" next to "Saved 14:35").
const _unloaded = new Set();
function _contentLoaded(id){ if(!_unloaded.delete(id)) return; if(_as && _as.id===id) _asPaint(_as); }
function _asPaint(a){
  const r=a&&a.refs; if(!r||!r.pillEl) return;
  const T=H().T; let txt='';
  const st=a.pill;
  if(st==='editing') txt=T('notes_st_editing');
  else if(st==='saving') txt=T('notes_st_saving');
  else if(st==='retrying') txt=T('notes_st_retrying');
  else if(st==='offline') txt=T('notes_st_offline');
  else if(st==='offline_kept') txt=T('notes_st_offline_kept'); // SC4: offline, but the draft is on this device
  else if(st==='conflict') txt=T('notes_st_conflict'); // SC3: paused — choose a version
  else if(st==='saved' && !_unloaded.has(a.id)){ const t=noteTimeHM(a.lastSavedAt); if(t) txt=T('notes_st_saved',{time:t}); } // T() bidi-wraps {time} in HE
  r.pillEl.className='vn2-pill vn2-pill-'+(st||'none');
  r.pillTxt.textContent=txt;
  r.pillEl.hidden=!txt;
}
function _asSetPill(a,st){ a.pill=st; _asPaint(a); }
function _asClearTimers(a){ clearTimeout(a.deb); clearInterval(a.tick); clearTimeout(a.retry); a.deb=a.tick=a.retry=null; }

// Snapshot of what to write, taken when a save STARTS. null = not writable now (clinical content still
// loading / failed to load → a write would put '' over the real note; note gone; locked AND published).
// SC3b (decision 4 — Lock freezes the clinical notes only): a locked, not-published note (pending_publish
// counts as not published) writes its parent SUMMARY only — never content/title/locked/is_draft.
function _asPayload(a){
  const r=a.refs; if(!r||!r.ta||r.ta.disabled) return null;
  const row=(H().DB.vaultNotes||[]).find(n=>n.id===a.id);
  if(!row||(row.locked&&row.published)) return null;
  if(!a.local && a.base==null) return null; // SC3: version not known yet → hold (never an unconditional write)
  const sum=(r.sta?r.sta.value:(row.summary||''));
  // SC6a: only the rich field writes the clinical text — content (the plain mirror) AND content_doc, always together.
  // The free tier's hidden field writes NEITHER (decision 25 R3a).
  const clin=_asClinNow(a), content=r.ta.value, contentDoc=clin?r.ta.doc:null; // F-19: + an unchanged plan-read-only field writes neither
  if(row.locked) return { row, summaryOnly:true, clin, title:row.title||'', content, contentDoc, summary:sum.trim()===''?null:sum };
  return { row, clin, title:_asTitleNow(a,row), content, contentDoc, summary:sum.trim()===''?null:sum,
    // SC1c/SC1e: a published or pending-publish note stays out of draft
    isDraft:!(row.published||row.pendingPublish) };
}
// A not-yet-created note (A3) is created only once it has real text.
function _asHasText(a,p){
  if((p.content||'').trim()||(p.summary||'').trim()) return true;
  const t=(p.title||'').trim(); return !!t && !isDefaultNoteTitle({ title:t }) && t!==(p.row.title||'').trim(); // SC5: a default title is not "text"
}

// → {r:'ok'|'fail'|'conflict'|'deleted', other?}
async function _asWrite(a,p){
  const { _supa, session } = H();
  const fields=p.summaryOnly ? { summary:p.summary }                                                       // SC3b
    : Object.assign({ title:p.title, summary:p.summary, is_draft:p.isDraft }, p.clin ? { content:p.content, content_doc:p.contentDoc } : {}); // SC6a: both or neither
  let res;
  if(a.local){
    // A3: the first save of a new note CREATES the row (SC1's error + one-row check); SC3: its version = base.
    const ts=new Date().toISOString(); _asOwnTok(a,ts);
    // SC5: a new note from a session carries its link on this first insert (and only here / on a move)
    const mk=(link)=>_supa.from('vault_notes').insert(Object.assign({ id:a.id, specialist_id:session?.specialistId||null, locked:false, published:false }, fields, { last_saved:ts, appointment_id:link }, H()._noteFkPayload(p.row)));
    let dup=false, taken=false;
    a.insertSent=true; // F-15: from here the row MAY exist until the server's answer says otherwise
    try{ let { data, error } = await mk(a.link||null).select('last_saved');
      // SC5 (decision 22): the link is refused (42501 — e.g. the session was soft-deleted) → save as a GENERAL note
      if(error && error.code==='42501' && a.link){ console.error('❌ autosave create note (session link refused → general):', error); a.link=null; _asSetRowLink(a,null); ({ data, error } = await mk(null).select('last_saved')); }
      const emsg=error ? (error.message||'')+' '+(error.details||'') : '';
      if(error){ if(error.code==='23505' && /one_per_session/.test(emsg)) taken=true; else if(error.code==='23505') dup=true; else console.error('❌ autosave create note:', error);
        if(error.code && !dup) a.insertSent=false; } // F-15: the server answered "not created"; a network error (no code) stays unknown
      else if(!data||data.length!==1) console.error('❌ autosave create note: expected 1 row, got '+(data?data.length:0));
      else { a.base=data[0].last_saved||ts; _asOwnTok(a,a.base); res={ r:'ok', ts }; }
    }catch(e){ console.error('❌ autosave create note:', e); }
    if(dup){
      // A lost response on a retry: the row exists. Ours (our own token) → continue as a conditional update;
      // anything else → never overwrite it.
      a.local=false;
      const c=await _asClassify(a,'autosave create note');
      if(c.r==='conflict' && _asIsOwn(a,c.other)){ a.base=c.other; res=await _asCondWrite(a,fields,'autosave note'); }
      else res=c;
    }
    if(taken) return { r:'taken' }; // SC5: another device already made a note for this session
    if(!res) res={ r:'fail' };
  } else {
    // Every later save is a CONDITIONAL update on the loaded version (never an upsert: a note deleted
    // elsewhere is never re-created, and a newer version is never overwritten silently).
    res=await _asCondWrite(a,fields,'autosave note');
  }
  if(res.r!=='ok') return res;
  if(p.clin && p.contentDoc) a.clinBase=JSON.stringify(p.contentDoc); // F-19: the server now has this doc
  H()._trackVaultNoteChange(a.id); // after the confirmed write only
  const DB=H().DB, ns=DB.vaultNotes, i=ns.findIndex(n=>n.id===a.id);
  if(i<0 && !p.summaryOnly && !tombHas(_bkUser(), a.id)){ // F-10: dropped from the list while the save was in flight → back in, from what was written (F-15: never a note deleted on this device)
    const ms=Number(String(a.id).replace(/^v_/,'')), sp=(H().session||{});
    ns.unshift({ id:a.id, specialistId:sp.specialistId||sp.id||null, childId:a.ctx.childId||null, specialistPatientId:a.ctx.specialistPatientId||null,
      title:p.title||'', content:p.clin?p.content:'', summary:p.summary||'', isDraft:p.isDraft!==false, locked:false, published:false, pendingPublish:false,
      lastSaved:a.base, date:H()._ymd(new Date(ms||Date.now())), appointmentId:a.link||null, session:null });
    DB.vaultNotes=ns;
  } else if(i>-1){ if(p.summaryOnly){ ns[i].summary=p.summary; } else { ns[i].title=p.title; if(p.clin) ns[i].content=p.content; ns[i].summary=p.summary; ns[i].isDraft=p.isDraft; } ns[i].lastSaved=a.base; delete ns[i].local; DB.vaultNotes=ns; } // SC6a: the mirror only — the doc never enters DB.vaultNotes
  a.local=false; a.lastSavedAt=a.base;
  return res;
}

function _asSave(a){
  if(!a||a.stopped) return Promise.resolve(false);
  if(a.conflict) return Promise.resolve(false);           // SC3: paused until the user chooses — no writes at all
  if(a.checking) return Promise.resolve(false);           // C4: a newer version is being checked; the save re-arms after
  if(a.saving){ a.queued=true; return a.saving; }
  if(!a.dirty) return Promise.resolve(true);
  clearTimeout(a.retry); a.retry=null;
  const p=_asPayload(a);
  if(!p) return Promise.resolve(false);                 // hold: stays dirty (saved once the content loads)
  const seq=a.editSeq;
  if(a.local && !_asHasText(a,p)){                       // nothing to create yet
    if(a.editSeq===seq){ a.dirty=false; _asMirror(a); _asSetPill(a,null); }
    return Promise.resolve(true);
  }
  if(navigator.onLine===false){ a.failing=true; _asSetPill(a,_asOffPill(a)); _asMirror(a); _asFailed(a); return Promise.resolve(false); } // open note: waits for 'online'
  _asSetPill(a,'saving');
  a.saving=(async()=>{
    let res={ r:'fail' };
    try{ res=await _asWrite(a,p); }catch(e){ console.error('❌ autosave note:',e); }
    a.saving=null;
    const ok=res.r==='ok';
    if(a.stopped){ _asMirror(a); return ok; }
    if(res.r==='deleted'){ _asConflict(a,'deleted'); return false; } // SC3
    if(res.r==='taken'){ _asSessionTaken(a); return false; }          // SC5
    if(res.r==='conflict'){ _asOnNewVersion(a,res.other); return false; } // SC3 C4: compare before asking
    if(ok){
      a.failing=false; a.backoffIdx=0; a.bgFails=0;
      if(a.released && a.editSeq===seq){ a.dirty=false; _bkClear(a); a.stopped=true; _asBg.delete(a); _asBgFlag(); _asClearDrafts(a.id); return true; } // A4: background save landed (no toast)
      if(a.editSeq===seq){ a.dirty=false; clearInterval(a.tick); a.tick=null; _asSetPill(a,'saved'); _bkClear(a); } // SC4: saved → drop the device copy
      else _asSetPill(a,'editing');                      // edited while saving: stays dirty
      _asMirror(a);
      // Exactly one follow-up for edits made during the save: now if a save was requested meanwhile,
      // else the pending 2 s debounce runs it.
      const q=a.queued; a.queued=false;
      if(a.dirty && (q || !a.deb)){ clearTimeout(a.deb); a.deb=null; return _asSave(a); }
      return true;
    }
    a.failing=true; a.queued=false; _asMirror(a);
    _asSetPill(a, navigator.onLine===false ? _asOffPill(a) : 'retrying');
    _asFailed(a);
    return false;
  })();
  _asMirror(a);
  return a.saving;
}

function _asRetryLater(a){ clearTimeout(a.retry); const d=_AS_BACKOFF[Math.min(a.backoffIdx,_AS_BACKOFF.length-1)]; a.backoffIdx++; a.retry=setTimeout(()=>{ a.retry=null; _asSave(a); },d); }
// A failed attempt. Open note: retry with backoff (offline: wait for 'online'). A note left without Back:
// retry with the same backoff even offline, and if it is still failing after the first retry, tell the
// user once (existing notes_err_save text) — its editor is gone, so there is no pill to show it.
function _asFailed(a){
  if(a.released){
    a.bgFails=(a.bgFails||0)+1;
    // F-5b (with U-2): offline with the latest edits confirmed on this device, there's nothing to warn about yet — the
    // 'online' retry saves it. Back online and still failing → the toast as before.
    if(a.bgFails>=2 && !a.toasted && !(navigator.onLine===false && _bkSafe(a))){ a.toasted=true; try{ H().toast(H().T('notes_err_save'),'error'); }catch(_){} }
    _asRetryLater(a);
  } else if(navigator.onLine!==false) _asRetryLater(a);
}

// Save now (Ctrl/⌘+S, blur/hidden, Lock/Publish/leave). Resolves true only when nothing is left unsaved.
async function _asFlush(a){
  if(!a||a.stopped) return true;
  if(a.conflict) return false;                             // SC3: unresolved conflict → nothing is saved
  clearTimeout(a.deb); a.deb=null;
  if(a.saving) await a.saving;
  if(!a.dirty) return true;
  clearTimeout(a.retry); a.retry=null;
  await _asSave(a);
  if(a.saving) await a.saving;
  return !a.dirty;
}

function _asEdited(){
  const a=_as; if(!a||a.stopped) return;
  a.editSeq++; a.dirty=true; _asMirror(a);
  _bkSchedule(a); // SC4: keep the unsaved draft on this device
  if(a.mine && !a.restoring){ a.mine=null; _asPaintConflict(a); } // SC3: the Restore chip goes on the first keystroke
  if(a.conflict) return;                                   // SC3: paused — keep the edit in memory, no save
  if(!a.saving) _asSetPill(a, a.failing ? (navigator.onLine===false?'offline':'retrying') : 'editing'); // offline → offline_kept once the device copy is written
  clearTimeout(a.deb); a.deb=setTimeout(()=>{ a.deb=null; if(!a.retry) _asSave(a); },_AS_DEBOUNCE);
  if(!a.tick) a.tick=setInterval(()=>{ if(a.dirty&&!a.saving&&!a.retry) _asSave(a); },_AS_TICK);
}
// Clinical content finished loading: an edit held back while it loaded can save now.
function _asContentReady(){ const a=_as; if(a&&a.dirty&&!a.saving&&!a.deb) a.deb=setTimeout(()=>{ a.deb=null; _asSave(a); },_AS_DEBOUNCE); }

// Retry now (failed state) / save now (dirty) on the window events.
function _asInstall(){
  if(_asInstalled) return; _asInstalled=true;
  const retryOne=(a)=>{ if(a&&a.dirty&&a.failing&&!a.saving&&!a.stopped){ clearTimeout(a.retry); a.retry=null; _asSave(a); } };
  const retryNow=()=>{ retryOne(_as); for(const b of [..._asBg]) retryOne(b); }; // A4: background notes too
  const saveNow=()=>{ const a=_as; if(a&&a.dirty&&!a.saving&&!a.stopped){ clearTimeout(a.deb); a.deb=null; clearTimeout(a.retry); a.retry=null; _asSave(a); } };
  window.addEventListener('online', retryNow);
  window.addEventListener('focus', retryNow);
  window.addEventListener('blur', saveNow);
  window.addEventListener('pagehide', saveNow);
  document.addEventListener('visibilitychange', ()=>{ if(document.visibilityState==='hidden') saveNow(); else retryNow(); });
  // A4: incl. a background note still unsaved. SC4: no prompt once the latest edits are safely on this device.
  const unsafe=(a)=>!!(a && !a.stopped && (a.dirty||a.saving) && !_bkSafe(a));
  window.addEventListener('beforeunload', (e)=>{ if(unsafe(_as) || [..._asBg].some(b=>unsafe(b) && !b.conflict)){ e.preventDefault(); e.returnValue=''; } });
}

// Bind the (re-)rendered editor's elements. Same note → keep the engine; another note → the previous
// note's pending changes are saved in the background (no UI to wait on) and a fresh engine starts.
function _asBind(note, refs){
  _asInstall();
  if(_as && _as.id!==note.id) _asRelease();
  if(!_as){
    const bg=[..._asBg].find(b=>b.id===note.id);
    if(bg){ _asBg.delete(bg); _asBgFlag(); bg.released=false; bg.toasted=false; bg.bgFails=0; _as=bg; } // A4: one writer per note
    else _as=_asNew(note);
  }
  _as.refs=refs;
  _asApplyFlags(_as, note);
  _asMirror(_as); _asPaint(_as); _asPaintConflict(_as);
}
function _asApplyFlags(a, note){
  if(note.locked && note.published){ a.stopped=true; _asClearTimers(a); a.dirty=false; } // published: summary read-only (as before)
  else if(note.locked && !a.summaryOnly){ a.summaryOnly=true; if(!a.dirty && !a.saving) a.pill=null; } // SC3b: summary-only; pill hidden until a summary save
}
function _asRelease(){
  const a=_as; _as=null; if(!a) return;
  a.mine=null; // SC3: leaving the note drops the Restore chip's copy
  a.released=true; clearTimeout(a.deb); clearInterval(a.tick); clearTimeout(a.retry); a.deb=a.tick=a.retry=null;
  if(a.refs) a.refs={ ...a.refs, pillEl:null, pillTxt:null };
  if(!a.stopped && (a.dirty||a.saving)){
    // A4: the draft stays in memory (a.refs holds the detached editor's text) and keeps retrying until saved.
    a.bgFails=0; a.toasted=false; a.backoffIdx=0; _asBg.add(a); _asBgFlag();
    if(!a.saving) _asSave(a);
  } else if(a.conflict && a.conflict.kind==='conflict' && a.dirty){
    _asBg.add(a); _asBgFlag(); // SC3: an unresolved conflict with my edits stays queued (no retries) until reopened
  } else { a.stopped=true; _asClearDrafts(a.id); } // SC3: nothing pending → reopening re-loads the content AND its version
}
// ── A2: Lock / Publish / Delete / leaving ──
// Stop the timers (a Lock/Publish write is about to carry the latest text itself).
function _asPause(){ const a=_as; if(a){ clearTimeout(a.deb); a.deb=null; clearTimeout(a.retry); a.retry=null; } }
// Delete: no timer, no queued follow-up, and no save left on the wire — else a late write could re-create it.
function _asCancel(){ const a=_as; if(!a) return Promise.resolve(); a.stopped=true; a.queued=false; _asClearTimers(a); return a.saving ? a.saving.then(()=>{},()=>{}) : Promise.resolve(); }
function _asResume(){ const a=_as; if(!a) return; a.stopped=false; _asMirror(a); if(a.dirty) a.deb=setTimeout(()=>{ a.deb=null; _asSave(a); },_AS_DEBOUNCE); }
// Lock/Publish wrote the note themselves: clean if nothing was typed since `seq`.
function _asMarkSaved(seq, ts){ const a=_as; if(!a) return; a.lastSavedAt=ts; a.local=false; if(a.editSeq===seq){ a.dirty=false; a.failing=false; _asClearTimers(a); _asSetPill(a,'saved'); _bkClear(a); } _asMirror(a); }
// SC3: Lock / Publish write the note conditionally on its version. A never-saved new note (no row, no
// version) keeps the old create-by-upsert, and its written token becomes the base.
async function _asStateWrite(a, fields, label, createQ){
  if(a && !a.local && a.base!=null) return _asCondWrite(a, fields, label);
  const ts=new Date().toISOString(); if(a) _asOwnTok(a,ts);
  const ok=await _writeNote(createQ(ts), label);
  if(ok && a){ a.base=ts; a.local=false; }
  return ok ? { r:'ok', ts } : { r:'fail' };
}
// ── bridge for index.js ──
export function notesFlush(){ return _asFlush(_as); }
// U-2: the note is left while its save fails (offline / server). If the device copy of the CURRENT edit is CONFIRMED
// written, leaving needs no dialog. Writes a pending copy now (no debounce) and resolves true only after a write for this
// exact edit has resolved; a failed write, an edit made meanwhile, no IndexedDB or a conflict → false.
// F-5 (Q6 A): a note SWITCH whose save failed may go ahead on the same confirmation.
export async function notesDeviceCopyConfirmed(){
  const a=_as; if(!a || a.stopped || a.conflict || !a.dirty || !bkAvailable()) return false;
  if(a.bkTimer){ clearTimeout(a.bkTimer); a.bkTimer=null; }
  if(a.bkOkSeq!==a.editSeq) _bkWrite(a);                 // queued after any write in flight (bkQ keeps the order)
  await a.bkQ;
  return _as===a && !a.conflict && _bkSafe(a);
}
export function notesLeaveState(){ const a=_as; return a ? { dirty:a.dirty, saving:!!a.saving, failing:a.failing, local:a.local, conflict:a.conflict&&a.conflict.kind } : null; }
// Leave → discard (the user chose Leave in the unsaved confirm): stop without saving.
// U-2 (Eytan, 2026-10-02): text kept on this device is never discarded by a dialog or a navigation. Discard drops the
// in-memory edit only; a device copy stays until a confirmed save (or deleting the note / Load theirs) clears it, and
// startup / 'online' recovery saves it.
export function notesDiscard(){ const a=_as; if(!a) return; a.stopped=true; a.dirty=false; _asClearTimers(a); _asMirror(a); }
// SC3: deleted elsewhere → leaving closes with no prompt and writes nothing (never re-create it).
export function notesDropDeleted(){ const a=_as; if(!a||!a.conflict||a.conflict.kind!=='deleted') return false; a.stopped=true; a.dirty=false; _asClearTimers(a); _asMirror(a); _bkClear(a); const DB=H().DB; DB.vaultNotes=(DB.vaultNotes||[]).filter(n=>n.id!==a.id); return true; }
// The homework offer (decision 7) for the note being left — captured before the sheet is torn down.
export function notesTakeLeaveOffer(){ const a=_as; return (a && a.refs && a.refs.offerHw) || null; }
export function noteHasEdits(id){ return !!(_as && _as.id===id && _as.editSeq>0); }
export function resetNotesAutosave(){ _asRelease(); }
// F-10: the ids of notes an editor engine still owns: the open note, and notes left with a save still to land (the
// background queue). A brand-new note among them has no server row yet but is NOT a leftover; the list and the
// refresh keep it listed until that first save lands.
// QS1: how many notes have text that hasn't reached the server yet — an editor save still pending (open or in the
// background queue) or a device copy waiting for recovery. Sign-out wipes the device copies (privacy rule), so it asks first.
export async function notesUnsentCount(){
  const ids=new Set();
  for(const a of [_as, ..._asBg]) if(a && !a.stopped && (a.dirty || a.saving)) ids.add(a.id);
  const uid=_bkUser();
  if(uid){ try{ (await bkAll(uid)).forEach(e=>ids.add(e.noteId)); }catch(e){ console.error('❌ unsent notes check:', e); } }
  return ids.size;
}
export function notesPendingIds(){ const out=[]; for(const a of [_as, ..._asBg]) if(a && !a.stopped) out.push(a.id); return out; }

// Build the editor root node for `note`. ctl = { onReflect, onClose }.
export function renderEditorBody(note, ctl){
  const { el, mkBtn, T, toast, openModal, openConfirm, DB, S, session, _supa } = H();
  const specId = session?.specialistId || null;
  const _ctx = notePatientCtx(); // SC5 L4: the sheet's own context when opened over the calendar
  const child = _ctx?.kind==='child' ? DB.children.find(c=>c.id===_ctx.child_id) : null;
  const _sp = _ctx?.kind==='spec_patient' ? (DB.specialistPatients||[]).find(p=>p.id===_ctx.specialist_patient_id) : null;
  const _vaultRemoved = (child?.connectionStatus||'approved')==='removed';
  const _patName = child?.name || _sp?.name || '';
  const _patAvatar = child?.avatar || _sp?.avatar_emoji || '🧒';
  const _patPhoto = child?.photo || null;
  const _aiOk = H()._hasSpecAiAccess();
  // F-53 F53-2: the plan check hasn't returned past 5 s, or it failed → 'pending': the clinical field shows Loading… and
  // can't be typed in; no read-only banner, no upgrade ghosts. Read at render; the sheet repaints when the check returns
  // (index.js onEntitlementChange). A note already open and editable is never switched to pending.
  const _aiPending = !_aiOk && !note.locked && !!(H()._specAiState && H()._specAiState()==='pending');
  const _isPhone = window.innerWidth < 768;
  const _isDesktop = window.innerWidth >= 1024;

  // ── Arc 1 SC-A1: "Create homework from note" ──────────────────────────────
  // One eligibility gate for BOTH the once-per-note save offer and the persistent
  // header button. Per SC-A1 decision #4 the affordance is HIDDEN (never disabled)
  // in every state where homework can't exist:
  //   · _ctx.kind!=='child'  → pre-parent (spec_patient) is structurally impossible (01 §C8)
  //   · note.locked          → finalized/read-only note
  //   · !_aiOk               → free tier has no id-bearing clinical surface
  //   · _vaultRemoved        → removed child connection (matches list-view.js homework gate)
  let _hwEligible = _ctx?.kind==='child' && _aiOk && !note.locked && !_vaultRemoved; // F-53: a remote lock turns it off in place

  // childId comes from the sheet CONTEXT (_ctx.child_id === S.activeChild), the same authoritative
  // source _hwEligible gates on — not note.childId (a derived/mapped field). When _hwEligible is true,
  // _ctx.kind==='child' so _ctx.child_id is always present, avoiding any note-mapping drift.
  // SC-A3: pass the note's CLINICAL text (the `ta` textarea — never `sta`, the parent summary)
  // so the modal can match templates against it. Fetch/matching happens inside the modal; here we
  // only hand over the freshest clinical text (live edits included).
  function _openHwCreate(){ window.HUD_HOMEWORK?.mountHomeworkCreateModal?.({childId:_ctx?.child_id, matchText: (ta&&ta.value)||''}); }
  function _markHwPromptShown(){
    // Persist + LS the once-per-note flag. Set ON SHOW, so both actions ("Create homework" /
    // "Not now") and navigate-away all count as shown-once. Separate owner UPDATE — never folded
    // into the save upsert (that would set it on every save and defeat "once ever").
    const ns=DB.vaultNotes,i=ns.findIndex(n=>n.id===note.id);
    if(i>-1 && !ns[i].homeworkPromptShown){ ns[i].homeworkPromptShown=true; DB.vaultNotes=ns; }
    if(session && session.id!=='demo'){
      _writeNote(_supa.from('vault_notes').update({homework_prompt_shown:true}).eq('id',note.id),'hw_prompt_shown'); // fire-and-forget; logs ❌ on failure/0 rows
    }
  }
  function _offerHomework(){
    _markHwPromptShown();
    // F17: this offer is an openModal `.overlay` — re()'s strip target — and the save's own
    // vault_notes upsert can fire a realtime → debounced re() while it's open. Guard with the
    // suppress counter (same pattern as _mmOpenModal). Opened AFTER ctl.onReflect() returns.
    S._suppressRefreshCount=(S._suppressRefreshCount||0)+1;
    openModal(T('vault_hw_offer_title'),(mb,close)=>{
      mb.appendChild(el('div',{style:{marginBottom:'20px',color:'#334155',fontSize:'14px',lineHeight:'1.5'}},[T('vault_hw_offer_body')]));
      const row=el('div',{style:{display:'flex',gap:'10px',justifyContent:'flex-end',flexWrap:'wrap'}});
      row.appendChild(mkBtn(T('push_not_now'),'btn-md btn-ghost',close));
      row.appendChild(mkBtn(T('vault_hw_create'),'btn-md btn-primary',()=>{ close(); _openHwCreate(); }));
      mb.appendChild(row);
    },440,()=>{ S._suppressRefreshCount=Math.max(0,(S._suppressRefreshCount||1)-1); });
  }
  function _maybeOfferHomework(){
    if(!_hwEligible) return;
    // Read the flag FRESH from DB, not the closure `note`: after ctl.onReflect() rebuilds the
    // editor, the save closure's `note` is stale (flag still false) and would re-fire next save.
    const _fresh=DB.vaultNotes.find(n=>n.id===note.id);
    if(!_fresh || _fresh.homeworkPromptShown) return;
    _offerHomework();
  }

  // N2b: peeks belong to one open note. Reset when the open note changes; a refresh()
  // re-render of the SAME note keeps them (module state survives the rebuild).
  if(_peekOwnerId !== note.id){ resetNotesPeeks(); _peekOwnerId = note.id; }

  const root = el('div',{class:'vn2-root'});

  // Clinical textarea — declared at function scope so the header's Save/Lock/AI/Publish
  // handlers read it by CLOSURE (N1 semantics). Critical: the free-tier textarea is hidden
  // and has NO id, so getElementById would return null and a Save/Publish would wipe content.
  // It is assigned in the clinical-pane section below, before any click can fire.
  let ta;

  // Notes SC2: a locked note keeps the state chip; any other note shows the autosave pill (Editing… /
  // Saving… / Saved HH:MM / retrying / offline), painted IN PLACE by the module engine — never re-rendered.
  let chipEl, pillEl=null, pillTxt=null;

  // Thin wrapper over the shared helper (used by the header chip + the past-sessions rows/window).
  const _computeChip = (n, isOpenNote, withTime=true)=> computeNoteChip(n, { isOpenNote, withTime });

  // T1 Tidy up — declared at function scope so the clinical-textarea section (built AFTER the
  // toolbar) can re-sync the button's disabled state once content loads / on every keystroke.
  // The real implementation is assigned inside the paid clinical-toolbar block below; no-op otherwise.
  let _syncTidyDisabled = ()=>{};
  let _stopDict = ()=>{}; // F-53: a remote lock stops a recording in progress (assigned with the mic below)
  let _showDictNotice = null, _hideDictNotice = ()=>{}; // SC6c D2: assigned with the clinical pane below

  // Notes SC1c — the parent summary being edited. S.vaultSummary is null while untouched (→ the note's stored
  // summary) and a string once edited, INCLUDING '' for an intentionally emptied summary, which must never fall
  // back to the old text (the `||` fallback resurrected it). Blank is written as NULL.
  const _curSummary = ()=> (S.vaultSummary ?? note.summary ?? '');
  const _summaryForWrite = ()=>{ const v=_curSummary(); return v.trim()==='' ? null : v; };
  // SC6a: the clinical columns a state write carries — content (the plain mirror) + content_doc from the rich field, always
  // together; the free tier's hidden field carries neither.
  const _clinFields = ()=> (ta && ta.isRich && !_clinUnchangedRO(_as && _as.id===note.id ? _as : null, ta)) ? { content: ta.value, content_doc: ta.doc } : {}; // F-19: see _asClinNow

  // Notes SC1 — inline error bar (under the header) with a Retry action. Shown in place — never via
  // onReflect/re() — so a failed save keeps the editor exactly as typed. One message at a time.
  const _errBar = el('div',{class:'vn2-errbar',role:'alert'});
  _errBar.hidden = true;
  const _clearErr = ()=>{ _errBar.hidden = true; _errBar.replaceChildren(); };
  const _showErr = (key, retry)=>{
    _errBar.replaceChildren(el('span',{class:'vn2-errbar-msg'},[T(key)]));
    if(retry){ const b=el('button',{class:'vn2-errbar-retry',type:'button'},[T('notes_retry')]); b.onclick=()=>{ _clearErr(); retry(); }; _errBar.appendChild(b); }
    _errBar.hidden = false;
  };
  // QF5 = A: a note opened OFFLINE whose text was never loaded on this device (no draft in memory, no device copy) shows an
  // offline card INSTEAD of the editor: no editor, no save pill / "Saved" time; it opens by itself when the connection
  // returns (the sheet's Back stays). Writes stay refused meanwhile (ta disabled, _loadFailed).
  let _offCard=null, _offRetry=null;
  const _showOfflineCard=(retry)=>{
    if(!_offCard) _offCard=el('div',{class:'vn2-offline-card',role:'status','data-offline-card':'1'},[el('div',{class:'vn2-offline-tx'},[T('notes_offline_not_loaded')])]);
    bodyEl.hidden=true; if(!_offCard.isConnected) bodyEl.after(_offCard);
    if(_as && _as.id===note.id) _asSetPill(_as,null); // never a "Saved" time for text that isn't on screen
    if(!_offRetry){ _offRetry=()=>{ window.removeEventListener('online',_offRetry); _offRetry=null; if(root.isConnected) retry(); }; window.addEventListener('online',_offRetry); }
  };
  const _hideOfflineCard=()=>{
    if(_offRetry){ window.removeEventListener('online',_offRetry); _offRetry=null; }
    if(!_offCard || !_offCard.isConnected) return;
    _offCard.remove(); bodyEl.hidden=false;
    if(_as && _as.id===note.id && !_as.dirty && !_as.local && _as.pill==null) _asSetPill(_as,'saved');
  };
  // True while the clinical content failed to load: the textarea stays disabled and every write
  // (Save/Lock/Publish) is refused until a load succeeds — never save over an unloaded note.
  let _loadFailed = false;
  const _notReady = ()=>{ if(!ta || !ta.disabled) return false; toast(T(_loadFailed?'notes_err_load':'vn2_still_loading'),'error'); return true; };

  let _pubBtnRef=null; // SC3: Publish is disabled during a conflict
  // ══════════════════════════════ HEADER ══════════════════════════════
  const header = el('div',{class:'vn2-header'});

  // Back control (replaces Minimise). Uses the centralized close path (unsaved confirm) —
  // shared with the backdrop click so both behave identically.
  // Header is CSS-responsive (back label / avatar / name hidden on phone via media query;
  // title+chip stack under 768) so a resize across a breakpoint needs no re-render.
  const backBtn = el('button',{class:'vn2-back'});
  backBtn.appendChild(el('span',{class:'vn2-chev','aria-hidden':'true'},['‹']));
  backBtn.appendChild(el('span',{class:'vn2-back-txt'},[T('vn2_back')]));
  backBtn.onclick = ()=>ctl.requestClose();
  header.appendChild(backBtn);

  // Patient avatar + name
  const avatar = el('div',{class:'vn2-avatar'});
  if(_patPhoto) avatar.appendChild(el('img',{src:_patPhoto,alt:''}));
  else avatar.textContent = _patAvatar;
  header.appendChild(avatar);
  header.appendChild(el('div',{class:'vn2-patient'},[_patName]));

  // Title + chip live in a wrap: inline on >=768, stacked (title over compact chip) on phone.
  const titleWrap = el('div',{class:'vn2-title-wrap'});
  const ti = el('input',{class:'vn2-title',dir:'auto',placeholder:' '});
  // F-27: the placeholder is a real element, so the date/time and the session type are each their own bidi part
  // (a Latin type in Hebrew no longer reorders the date) and it wraps instead of being clipped. It shares one grid
  // cell with the input; CSS hides it while the input has text (:placeholder-shown — the input's placeholder is ' ').
  const tiPh = el('span',{class:'vn2-title-ph','aria-hidden':'true'});
  const tiBox = el('div',{class:'vn2-title-box'},[ti, tiPh]);
  // SC5 L2 (queue item 5): a default title shows as '' — the placeholder is the date computed from the data
  // (the session's date + time, else the written date); a custom title shows as typed. Stored titles are never rewritten.
  ti.value = (S._vaultDraftTitle&&S._vaultDraftTitle[note.id]!=null)?S._vaultDraftTitle[note.id]:titleForInput(note);
  ti.disabled = note.locked;
  // SC5 L3: the session chip (mock v3) — which session this note is for; opens the session picker (picker.js).
  const _sessBtn = el('button',{class:'vn2-sess',type:'button',title:T('notes_pick_hint'),'aria-label':T('notes_pick_hint'),'aria-haspopup':'menu','aria-expanded':'false'});
  _sessBtn.onclick = (e)=>{ e.stopPropagation(); toggleSessionMenu(_sessBtn, note, ctl); };
  const _paintLink = ()=>{ // the chip + the title placeholder from the data (in place — a link change never re-renders)
    const cur=(DB.vaultNotes||[]).find(n=>n.id===note.id)||note;
    const typ=noteSessionType(cur), lbl=noteDateLabel(cur);
    tiPh.replaceChildren(el('bdi',{dir:'auto'},[lbl]), ...(typ ? [' · ', el('bdi',{dir:'auto'},[typ])] : []));
    ti.setAttribute('aria-label', lbl + (typ ? ' · '+typ : ''));
    paintSessionChip(_sessBtn, note);
  };
  _paintLink();
  ti.oninput = e=>{ if(!S._vaultDraftTitle)S._vaultDraftTitle={}; S._vaultDraftTitle[note.id]=e.target.value; _asEdited(); }; // SC2: autosave
  titleWrap.appendChild(_sessBtn);
  titleWrap.appendChild(tiBox);
  header.appendChild(titleWrap);

  if(note.locked){
    // State chip (single, priority order) — via the shared helper
    const _hc = _computeChip(note, true);
    chipEl = el('span',{class:'vn2-chip',style:{background:_hc.bg,color:_hc.fg}},[_hc.label]);
    titleWrap.appendChild(chipEl);
  }
  // SC2 pill; SC3b: a locked, not-published note shows it NEXT TO the locked chip (summary saves only)
  if(!note.locked || !note.published){
    pillTxt = el('span',{class:'vn2-pill-txt'});
    pillEl = el('span',{class:'vn2-pill',role:'status','aria-live':'polite'},[el('span',{class:'vn2-pill-dot','aria-hidden':'true'}), pillTxt]);
    pillEl.hidden = true;
    titleWrap.appendChild(pillEl);
  }

  // Header actions (end side)
  const hactions = el('div',{class:'vn2-hactions'});

  // Notes SC2 (decision 6): no Save button — the note autosaves; Ctrl/⌘+S saves now (index.js → notesFlush).

  // ── Lock (secondary; _aiOk && !locked — same as N1) ──
  let lockBtn;
  const _lockVisible = _aiOk && !note.locked;
  if(_lockVisible){
    lockBtn = mkBtn(T('vault_lock_notes'),'btn-sm btn-ghost',()=>{
      if(_notReady()) return;
      if(_as && _as.conflict) return; // SC3: disabled until the conflict is resolved
      const hasSummary=!!_curSummary().trim(); // SC1c
      const _doLock=async()=>{
        if(_notReady()) return;
        _clearErr();
        // Notes SC2 (A2): first save any pending/in-flight autosave; if that fails, stop with the SC1 error.
        if(!(await notesFlush())){ _showErr('notes_err_lock', _doLock); return; }
        _asPause();
        const _title=_as?_asTitleNow(_as,(DB.vaultNotes||[]).find(n=>n.id===note.id)||note):ti.value; // SC5
        const _content=ta.value, _clin=_clinFields(); // SC6a: content + content_doc together (Lock is paid-only → always the rich field)
        const _summary=_summaryForWrite(); // SC1c
        // SC3: conditional on the note's version (a newer version elsewhere → the conflict flow, never an overwrite)
        const _res=await _asStateWrite(_as,Object.assign({title:_title,summary:_summary,is_draft:false,locked:true},_clin),'lock note',
          (ts)=>_supa.from('vault_notes').upsert(Object.assign({id:note.id,specialist_id:specId,title:_title,summary:_summary,is_draft:false,locked:true,published:note.published||false,last_saved:ts,appointment_id:(_as&&_as.link)||null},_clin,H()._noteFkPayload(note)),{onConflict:'id'}));
        if(_res.r==='conflict'||_res.r==='deleted'){ _asConflict(_as,_res.r,_res.other); return; }
        // Notes SC1: show Locked only after a confirmed 1-row write; otherwise stay unlocked + Retry.
        if(_res.r!=='ok'){ _asResume(); _showErr('notes_err_lock', _doLock); return; }
        H()._trackVaultNoteChange(note.id); // SC2: after the confirmed write
        if(_as){ _as.dirty=false; _asClearTimers(_as); } // SC3b: after Lock only the parent summary autosaves (summary-only payload)
        const ns=DB.vaultNotes,i=ns.findIndex(n=>n.id===note.id);
        if(i>-1){ns[i].title=_title;if(_clin.content!=null)ns[i].content=_content;ns[i].summary=_summary;ns[i].locked=true;ns[i].isDraft=false;DB.vaultNotes=ns;}
        if(S._vaultDraftContent)delete S._vaultDraftContent[note.id];if(S._vaultDraftTitle)delete S._vaultDraftTitle[note.id];if(S._vaultTidyRaw)delete S._vaultTidyRaw[note.id];
        S.vaultHasUnsaved=false;ctl.onReflect();toast(T('vault_locked_toast'));
      };
      if(!hasSummary){
        openConfirm(T('vault_no_summary_warn'),T('vault_no_summary_msg'),true,()=>{
          openConfirm(T('vault_lock_confirm'),T('vault_lock_msg'),true,_doLock);
        });
      } else {
        openConfirm(T('vault_lock_confirm'),T('vault_lock_msg'),true,_doLock);
      }
    });
  }

  // ── Delete (overflow; only when !published AND !locked — matches N1, where Delete lived
  //    inside the !note.locked action row so locked notes had no Delete) ──
  let delBtn;
  if(!note.published && !note.locked){
    delBtn = mkBtn(T('btn_delete'),'btn-sm btn-ghost',()=>{
      _closeMenu();
      openConfirm(T('vault_delete_note'),T('vault_delete_note_msg'),true,async()=>{
        // Notes SC2 (A2): cancel every autosave timer + queued save and let an in-flight save land BEFORE
        // deleting — a late autosave must never re-create the deleted note.
        await _asCancel();
        const _neverSaved=!!(_as && _as.local); // A3: a new note that was never written has no row
        // F-15: ...unless its first INSERT was sent and its answer never came (a lost response) — the row MAY exist.
        const _unknown=_neverSaved && !!(_as && _as.insertSent);
        const _uid=_bkUser();
        if(session&&session.id!=='demo'&&_neverSaved){
          // F-15 (QF15-1 A, QF15-2): delete it on this device now; the tombstone (24 h) hides it if it ever shows up and
          // deletes it on the server — now if online (0 rows = never created = fine), else when back online.
          tombAdd(_uid, note.id, !_unknown);
          if(_unknown) tombFlush(_uid, _supa);
        }
        if(session&&session.id!=='demo'&&!_neverSaved){
          // Notes SC1: an RLS-filtered DELETE returns 0 rows with no error — check the row count.
          // Hard delete first; soft-delete fallback; if neither is confirmed, keep the note + editor.
          let _gone=await _writeNote(_supa.from('vault_notes').delete().eq('id',note.id),'delete note');
          if(!_gone){
            _gone=await _writeNote(_supa.from('vault_notes').update({title:'__DELETED__',content:'',content_doc:null,summary:'',published:false,last_saved:new Date().toISOString()}).eq('id',note.id),'soft-delete note'); // SC3: new version → the other device sees "deleted elsewhere"
          }
          if(!_gone){ _asResume(); toast(T('hw2_delete_error'),'error'); return; }
          H()._trackVaultNoteChange(note.id); // SC2: after the confirmed write
          tombAdd(_uid, note.id, true); // F-15: a stale refresh / live update can't bring it back either
        }
        const ns=DB.vaultNotes.filter(n=>n.id!==note.id);DB.vaultNotes=ns;
        if(S._vaultDraftContent)delete S._vaultDraftContent[note.id];if(S._vaultDraftTitle)delete S._vaultDraftTitle[note.id];if(S._vaultTidyRaw)delete S._vaultTidyRaw[note.id];
        if(_as && _as.id===note.id) _bkClear(_as); // SC4: deleted → no device copy (never re-created)
        S.vaultHasUnsaved=false;ctl.onClose();toast(T('vault_note_deleted'));
      });
    });
  }

  // Place Save + Lock (desktop/tablet) in header; overflow holds Delete (+ Lock on phone).
  if(lockBtn && !_isPhone) hactions.appendChild(lockBtn);

  // Arc 1 SC-A1: persistent "Create homework" — reachable wherever the feature is possible
  // (NO once-limit; the later path after the offer). Inline in the header ≥768; folds into the
  // ⋯ overflow on phone ("always reachable" per the SC-A1 ruling). childId is guaranteed present
  // by _hwEligible (_ctx.kind==='child'). _closeMenu is defined just below (called at click time).
  let hwBtn=null;
  if(_hwEligible){
    hwBtn = mkBtn(T('vault_hw_create'),'btn-sm btn-ghost',()=>{ _closeMenu(); _openHwCreate(); });
    if(!_isPhone) hactions.appendChild(hwBtn);
  }

  // ⋯ overflow menu
  let _menu=null, overflowBtn=null;
  const _closeMenu=()=>{ if(_menu){_menu.remove();_menu=null;} };
  const _menuItems = [];
  if(_isPhone && lockBtn) _menuItems.push(lockBtn);
  if(_isPhone && hwBtn) _menuItems.push(hwBtn);
  if(delBtn) _menuItems.push(delBtn);
  // F-63c: one place creates / re-attaches / removes the ⋯ button so a resize across 768 (reslotHeaderActions) can use it too.
  const _syncOverflow=()=>{
    if(!_menuItems.length){ if(overflowBtn){ _closeMenu(); overflowBtn.remove(); } return; }
    if(!overflowBtn){
      overflowBtn = el('button',{class:'vn2-overflow','aria-label':T('vn2_more'),'title':T('vn2_more')},['⋯']);
      overflowBtn.onclick=(e)=>{
        e.stopPropagation();
        if(_menu){_closeMenu();return;}
        _menu = el('div',{class:'vn2-menu'});
        _menuItems.forEach(b=>_menu.appendChild(b));
        hactions.appendChild(_menu);
        setTimeout(()=>document.addEventListener('click',_closeMenu,{once:true}),0);
      };
    }
    if(overflowBtn.parentNode!==hactions) hactions.appendChild(overflowBtn);
  };
  _syncOverflow();
  header.appendChild(hactions);
  root.appendChild(header);
  // F-63: the sheet scrolls as a whole and the header is sticky, so the formatting toolbar and the side panes stick just under it.
  // The header can wrap onto several rows (F-25), so its real height is measured and published on the overlay as --vn2-hdr.
  try{ if(typeof ResizeObserver==='function'){ new ResizeObserver(()=>{ const ovEl=header.closest('.vn2-overlay'); if(ovEl) ovEl.style.setProperty('--vn2-hdr', Math.round(header.getBoundingClientRect().height)+'px'); }).observe(header); } }catch(_){}
  root.appendChild(_errBar); // Notes SC1
  // Notes SC3 (C2): conflict / deleted-elsewhere banner, or the "Restore my version" chip — above the panes.
  const _conflictSlot = el('div',{class:'vn2-conflict-slot'});
  root.appendChild(_conflictSlot);

  // ══════════════════════════════ BODY ══════════════════════════════
  const bodyEl = el('div',{class:'vn2-body'});

  // Phone segmented switcher (hidden >=768 via CSS)
  const clinicalHasText = ()=> (S._vaultDraftContent&&S._vaultDraftContent[note.id]!=null&&S._vaultDraftContent[note.id].length>0) || (note.content&&note.content.length>0);
  const parentHasText = ()=> !!_curSummary().length; // SC1c
  const _clinDot = el('span',{class:'vn2-dot'+(clinicalHasText()?' vn2-dot-filled':'')});
  const _parDot = el('span',{class:'vn2-dot'+(parentHasText()?' vn2-dot-filled':'')});
  const segClinical = el('button',{class:'vn2-seg vn2-seg-active'},[]);
  segClinical.appendChild(el('span',{},[T('vn2_clinical')])); segClinical.appendChild(_clinDot);
  const segParent = el('button',{class:'vn2-seg'},[]);
  segParent.appendChild(el('span',{},[T('vn2_parent_summary')])); segParent.appendChild(_parDot);
  const switcher = el('div',{class:'vn2-switcher'},[segClinical,segParent]);

  // ── CLINICAL PANE ──
  const clinPane = el('div',{class:'vn2-pane vn2-pane-clinical'});
  const clinHd = el('div',{class:'vn2-pane-hd'});
  const clinLabel = el('div',{class:'vn2-pane-hd-label'});
  clinLabel.appendChild(el('span',{'aria-hidden':'true'},['🔒']));
  clinLabel.appendChild(el('span',{},[T('vn2_clinical')]));
  clinLabel.appendChild(el('span',{class:'vn2-badge vn2-badge-private'},[T('vn2_private')]));
  clinHd.appendChild(clinLabel);
  // dictation / lock-dictate controls live in this actions container (mic + toggle share parent)
  const clinActions = el('div',{class:'vn2-pane-actions'});

  if(_aiOk && !note.locked){
    // T1 Tidy up state. `_getIsRec` is overridden inside the dictation block (below) once `_isRec`
    // exists; it stays ()=>false when speech recognition is unavailable (e.g. Firefox) so Tidy still works.
    let _getIsRec=()=>false; let _tidyBusy=false; let tidyBtn=null;
    _syncTidyDisabled=()=>{ if(tidyBtn) tidyBtn.disabled = _tidyBusy || _getIsRec() || dictBusy() || !ta || ta.disabled || !ta.value || !ta.value.trim(); }; // SC6c: + a clean-up in flight
    const _SpeechRec=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(_SpeechRec){
      // SC6c D3 (decision 28.10): natural dictation — the only spoken command left is "stop recording" (rich-dictation.js);
      // the AI pass on Stop does the punctuation / lists / headings. No "Commands ?" sheet.
      const micBtn=el('button',{style:{background:'none',border:'1.5px solid var(--teal)',borderRadius:'8px',padding:'3px 10px',fontSize:'.72rem',fontWeight:700,color:'var(--teal)',cursor:'pointer',display:'flex',alignItems:'center',gap:'4px'}},[T('vault_voice_start')]);
      let _recog=null;let _isRec=false;let _shouldContinue=false;
      _getIsRec=()=>_isRec; // Tidy reads the live recording flag through this
      let _dict=null; // SC6a R5: the dictation session into the rich field (anchored insertion, interim = decoration)
      const _setMicUI=(recording)=>{
        micBtn.textContent=recording?T('vault_voice_stop'):T('vault_voice_start');
        micBtn.style.borderColor=recording?'var(--coral)':'var(--teal)';
        micBtn.style.color=recording?'var(--coral)':'var(--teal)';
        const _slBtns=micBtn.parentElement?.querySelectorAll('[data-speech-lang]');
        if(_slBtns)_slBtns.forEach(b=>{
          const isActive=b.dataset.speechLang===H()._speechLang;
          b.style.background=isActive?'var(--teal)':'transparent';
          b.style.color=isActive?'#fff':'var(--teal)';
        });
        _syncTidyDisabled(); // Tidy is disabled while recording
      };
      // SC6c D2: the clean-up UI — a "Cleaning up…" chip by the mic while a pass is in flight; a small notice bar in the
      // clinical pane (AI-edited + Undo, 6 s / kept / offline), hidden on the next edit.
      const _dictChip=el('span',{class:'vn2-dict-status',role:'status','aria-live':'polite',hidden:''},[T('notes_dict_working')]);
      const _dictUi={
        working:(on)=>{ _dictChip.hidden=!on; _syncTidyDisabled(); },
        notice:(kind,o)=>{ if(typeof _showDictNotice==='function') _showDictNotice(kind,o); },
      };
      const _cleanupRec=()=>{
        _isRec=false;
        _setMicUI(false);
        if(_dict){
          const _sid=_dict.sid, _dict_ai=_dict.ai; _dict.end(); _dict=null;
          // SC6c: the raw words are saved first (autosave flush), then the session's runs are cleaned up — ONE AI request per run
          if(ta && ta.isRich && _sid){ try{ notesFlush(); }catch(_){} runDictationPass(ta,_sid,_dictUi,{ off:!_dict_ai }); } // off → no request, one console line
        }
      };
      const _continuous=dictContinuousOK(); // SC6c D10 (decision 33.2): desktop Chrome only; everything else stays false
      const _makeRecog=()=>{
        const r=new _SpeechRec();r.continuous=_continuous;r.interimResults=true;r.lang=H()._speechLang;
        // SC6a R5: each FINAL segment → one history step at the anchor; interim → a decoration only (never in the doc,
        // never saved). Autosave runs off the final inserts (the editor's update).
        // SC6c D10: in continuous mode the results list GROWS across the session — each final index is inserted once
        // (continuous=false keeps the pre-D10 handling: every final result is inserted, then the 1 s duplicate filter);
        // the provisional text still pending when the engine ends is kept as final (decision 33.1).
        const done=new Set(); let pending='';
        const _final=(text)=>{ if(_dict && _dict.final(text)==='stop'){ _shouldContinue=false; try{ r.stop(); }catch(_){} } };
        r.onresult=ev=>{
          if(!_dict) return;
          let _interim='';
          for(let i=ev.resultIndex;i<ev.results.length;i++){
            if(ev.results[i].isFinal){ if(_continuous){ if(done.has(i)) continue; done.add(i); } _final(ev.results[i][0].transcript); } // index tracking only where the list grows
            else {_interim+=ev.results[i][0].transcript;}
          }
          pending=_interim.trim();
          if(_dict) _dict.interim(_interim);
        };
        r.onend=()=>{
          if(pending && _dict){ const p=pending; pending=''; _final(p); } // decision 33.1: never drop the unfinished phrase
          if(_shouldContinue){
            if(_dict) _dict.interim('');
            // decision 33.2: restart at once (no timer) — the fallback for every platform, continuous or not
            try{_recog=_makeRecog();_recog.start();}
            catch(e){
              setTimeout(()=>{ // only when the immediate restart threw: one retry
                if(!_shouldContinue)return;
                try{_recog=_makeRecog();_recog.start();}
                catch(e2){console.error('Voice restart failed:',e2);_shouldContinue=false;_cleanupRec();}
              },500);
            }
            return;
          }
          _cleanupRec();
        };
        r.onerror=e=>{
          if(e.error==='aborted')return;
          if(e.error==='no-speech'&&_shouldContinue)return;
          if(e.error==='not-allowed'||e.error==='service-not-available'){
            _shouldContinue=false;_cleanupRec();
            toast(T('vault_mic_error')+e.error,'error');
            return;
          }
          console.error('Speech error:',e.error);
        };
        return r;
      };
      const _stopRec=()=>{_shouldContinue=false;if(_recog)try{_recog.stop();}catch(e){}};
      _stopDict=()=>{ if(_isRec) _stopRec(); };
      micBtn.onclick=()=>{
        if(_isRec){_stopRec();return;}
        if(_notReady()) return; // Notes SC1: never dictate into a still-loading / failed-load (disabled) field
        if(!(ta && ta.isRich && ta.editor)) return;
        const _ai=dictAiOn(); // SC6c D3: read per session — off → no clean-up, and the old punctuation words apply
        _dict=startDictation(ta,{ punct:!_ai }); if(!_dict) return;
        _dict.ai=_ai;
        if(_ai && (dictHintDue() || dictTipDue())){ dictHintSeen(); dictTipSeen(); if(typeof _showDictNotice==='function') _showDictNotice('hint',{ lines:[['notes_dict_production_tip']] }); } // decisions 28.11 + 37.7: once per device, the production tip as its second line
        _shouldContinue=true;_isRec=true;
        _setMicUI(true);
        _recog=_makeRecog();
        try{_recog.start();}catch(e){_shouldContinue=false;_cleanupRec();toast(T('vn2_mic_failed'),'error');}
      };
      clinActions.appendChild(micBtn);
      clinActions.appendChild(_dictChip); // SC6c
      if(H()._availableLangs.length>1){
        const _slWrap=el('div',{style:{display:'inline-flex',gap:'2px',marginLeft:'4px'}});
        const _mkSlBtn=(code,label)=>{
          const b=el('button',{'data-speech-lang':code,style:{padding:'2px 7px',borderRadius:'6px',border:'1px solid var(--teal)',fontSize:'.65rem',fontWeight:700,cursor:'pointer',background:H()._speechLang===code?'var(--teal)':'transparent',color:H()._speechLang===code?'#fff':'var(--teal)'}},[label]);
          b.onclick=(e)=>{
            e.stopPropagation();
            H()._speechLang=code;
            _setMicUI(_isRec);
            if(_isRec&&_recog){try{_recog.stop();}catch(ex){}}
          };
          return b;
        };
        _slWrap.appendChild(_mkSlBtn('en-US','EN'));
        _slWrap.appendChild(_mkSlBtn('he-IL','עב'));
        clinActions.appendChild(_slWrap);
      }
    }

    // ── T1 Tidy up (sparkles) — after the dictation controls ──
    const _tidyLbl=el('span',{style:{marginInlineStart:'4px'}},[T('vn2_tidy')]);
    tidyBtn=el('button',{style:{background:'none',border:'1.5px solid var(--teal)',borderRadius:'8px',padding:'3px 10px',fontSize:'.72rem',fontWeight:700,color:'var(--teal)',cursor:'pointer',display:'flex',alignItems:'center',gap:'2px'}},[el('span',{'aria-hidden':'true'},['✨']),_tidyLbl]);
    tidyBtn.onclick=async()=>{
      if(_getIsRec())return;
      if(!ta||ta.disabled){toast(T('vn2_still_loading'),'error');return;}
      if(!ta.value||!ta.value.trim()){toast(T('vault_write_first'),'error');return;}
      // Tidy guards T3 (decision 35): Tidy works on the note built from the doc — every heading as "<label>:" (the 8 and
      // the specialist's own), lists as in the mirror. Formatting is still dropped (decision 25.8) — now said in the
      // result bar, only when a tidy was applied to a note that had formatting (35.11).
      const _in=ta.isRich ? docToTidyText(ta.doc) : { text:ta.value, headings:[] };
      const _sent=_in.text; // decision 35.9: the result applies only if the note still reads exactly this
      if(_sent.length>DICT_MAX_CHARS){ if(typeof _showDictNotice==='function') _showDictNotice('tidy_too_long'); return; } // 35.12: no request
      const _hadMarks=ta.isRich && docHasInlineMarks(ta.doc), _hadNesting=ta.isRich && docHasNestedList(ta.doc);
      _tidyBusy=true;_syncTidyDisabled();
      _tidyLbl.textContent=T('vault_ai_generating');
      const _t0=performance.now(); let _res='failed', _why=null;
      try{
        const _sess=await _supa.auth.getSession();const _tok=_sess?.data?.session?.access_token;
        if(!_tok){toast(T('vn2_session_expired'),'error');_tidyBusy=false;_tidyLbl.textContent=T('vn2_tidy');_syncTidyDisabled();return;}
        // QA-46 (decision 12): NO `lang` — the server's "same language as the input" rule applies (an English note stays English)
        const resp=await fetch('/api/ai-summary',{method:'POST',headers:{'Content-Type':'application/json','Authorization':'Bearer '+_tok},body:JSON.stringify({action:'tidy',text:_sent,inputHeadings:_in.headings})});
        const result=await resp.json().catch(()=>({}));
        _why=result && result.reason || null;
        if(resp.status===403){
          H()._showSpecAiUpgradeModal('Session Notes');   // QA-01: the endpoint's only 403 is "No AI access" → upgrade/trial, not a generic toast
        } else if(resp.status===413 || (result && result.reason==='too_long')){
          _res='kept'; if(typeof _showDictNotice==='function') _showDictNotice('tidy_too_long');
        } else if(resp.ok && result && result.cleaned===false){
          _res='kept'; if(typeof _showDictNotice==='function') _showDictNotice(result.reason==='production' ? 'tidy_production_kept' : 'tidy_kept'); // a guard failed: nothing applied, the note (and its formatting) as it was
        } else if(!resp.ok||!result||result.cleaned!==true||typeof result.text!=='string'){
          toast((resp.status===429&&result&&result.error)?result.error:T('vn2_tidy_failed'),'error');
        } else if(!ta.isRich || ta.destroyed || ta.disabled || ta.readOnly){
          toast(T('vn2_tidy_failed'),'error');           // the note changed state while tidying — nothing applied
        } else if(docToTidyText(ta.doc).text!==_sent){
          // decision 35.9 (T0): the specialist typed (or anything else changed the text) while Tidy was working — applying the
          // result would overwrite that. Apply nothing; the note stays exactly as it is now.
          _res='dropped'; _why='text_changed'; toast(T('notes_tidy_changed'),'info',6000);
        } else if(_tidySame(result.text,_sent)){
          _res='unchanged'; toast(T('notes_tidy_nothing'),'info'); // decision 35.10: nothing to apply
        } else {
          // SC6a (decision 25.9): headings + lists rebuilt from the result (the note's own headings too, 35.7); ONE
          // transaction = ONE undo step (Redo re-applies it). No re-render; the input path autosaves it.
          ta.setDoc(plainToDoc(result.text,{ extraHeadings:_in.headings, srcLines:_in.text.split('\n'), lineDirs:_in.lineDirs }),{ undoable:true }); // F-55b: a forced direction survives Tidy
          ta.dispatchEvent(new Event('input'));
          _res='applied';
          const api=window.HUD_NOTES_RICH, ed=ta.editor, end=ed ? ed.state.doc.content.size : 0;
          const nf=Array.isArray(result.numberFixes)?result.numberFixes:[], wf=Array.isArray(result.wordFixes)?result.wordFixes:[], ha=Array.isArray(result.headingsAdded)?result.headingsAdded.filter(h=>typeof h==='string'&&h.trim()):[];
          try{
            if(ed && nf.length) api.markNumberFixes(ed,0,end,nf.map(f=>Number(f.to)));
            if(ed && wf.length) api.markTextFixes(ed,0,end,wf.map(f=>f.to));
            if(ed && ha.length) api.markHeadingNodes(ed,ha.filter(h=>!_in.headings.includes(h.trim())));
          }catch(e){ console.error('❌ tidy highlights:',e); }
          const lines=[];
          if(nf.length) lines.push(['notes_tidy_numfix',{ list:numfixList(nf) }]);
          if(wf.length) lines.push(['notes_tidy_wordfix',{ list:wordfixList(wf) }]);
          if(ha.length) lines.push(['notes_tidy_headings',{ list:ha.map(h=>h.trim()).join(', ') }]);
          if(_hadMarks) lines.push(['notes_tidy_marks_lost']);
          if(_hadNesting) lines.push(['notes_tidy_lists_flat']);
          if(typeof _showDictNotice==='function') _showDictNotice('tidied',{ lines, undo:()=>{ try{ ta.editor && ta.editor.commands.undo(); }catch(_){} } });
        }
      }catch(e){console.error('tidy:',e);toast(T('vn2_tidy_failed'),'error');}
      try{ console.info('[notes] tidy '+JSON.stringify({ ms:Math.round(performance.now()-_t0), result:_res, reason:_why })); }catch(_){} // no content
      _tidyBusy=false;_tidyLbl.textContent=T('vn2_tidy');_syncTidyDisabled();
    };
    clinActions.appendChild(tidyBtn);
    _syncTidyDisabled();                                 // initial state (disabled until content loads)

    // SC6a (decision 25.9): no "Restore original" chip — one Undo restores the pre-tidy text.
  } else if(!_aiOk && !_aiPending && !note.locked){
    const micLock=el('button',{style:{background:'none',border:'1.5px solid var(--mint-l)',borderRadius:'8px',padding:'3px 10px',fontSize:'.72rem',fontWeight:600,color:'var(--slate-l)',cursor:'pointer',display:'flex',alignItems:'center',gap:'4px'}},[T('vault_dictate_locked')]);
    micLock.onclick=()=>H()._showSpecAiUpgradeModal(T('vault_voice_start'));
    clinActions.appendChild(micLock);
    // Tidy up as a ghost affordance → same upgrade modal (parity with the parent-pane Generate ghost)
    const tidyGhost=el('button',{style:{background:'none',border:'1px solid var(--mint-l)',borderRadius:'8px',padding:'3px 10px',fontSize:'.72rem',fontWeight:600,color:'var(--slate-l)',cursor:'pointer',display:'flex',alignItems:'center',gap:'2px'}},[el('span',{'aria-hidden':'true'},['✨']),el('span',{style:{marginInlineStart:'4px'}},[T('vn2_tidy')])]);
    tidyGhost.onclick=()=>H()._showSpecAiUpgradeModal('Session Notes');
    clinActions.appendChild(tidyGhost);
  }
  const _clinEditActs = [...clinActions.children]; // F-53: the mic / Tidy (or their ghosts) — a remote lock removes them in place
  clinHd.appendChild(clinActions);
  clinPane.appendChild(clinHd);

  const clinScroll = el('div',{class:'vn2-scroll'});
  if(note.locked){
    clinScroll.appendChild(el('div',{class:'vn2-locked-banner'},[T('vault_locked_readonly')]));
  }
  // F-19 (QP-6, QR-a A): without plan access the clinical notes are READ-ONLY — shown in the same rich field as a locked
  // note (never hidden behind an upgrade card). Loading, the device copy and the autosave engine are exactly the paid
  // path's, so an edit made before the lapse (open, background queue, device copy) still saves; the server never
  // refuses a note save (QP-4). Only new typing is off.
  const _clinRO = !_aiOk && !_aiPending && !note.locked; // F-53: no plan (not merely not known yet)
  let _rb=null, _tbEl=null; // F-53: painted away in place by a remote lock
  if(_clinRO){
    _rb=H()._readOnlyBanner?.(T('notes_readonly_line'),T('vault_ai_notes_desc'),'Session Notes'); // F-19 SC4: ended → notes_readonly_line; never tried → the existing description
    if(_rb) clinScroll.appendChild(_rb);
  }
  {
    const taWrap = el('div',{class:'vn2-ta-wrap vn2-rich-wrap'});
    // SC6a R3a: the rich clinical field (Tiptap) — ONE instance per open note, kept across re-renders of that note.
    const field=_richFieldFor(note);
    ta=field;
    field.readOnly=!!note.locked||_clinRO||_aiPending;
    field.planRO=_clinRO||_aiPending; // F-19: read-only for lack of a plan (the clinical columns are written only if the doc changed); F-53: also while pending
    // F-53: while pending the field shows the existing "Loading…" over a hidden text (styles.js [data-pending])
    if(_aiPending) field.el.setAttribute('data-pending', T('vn2_loading')); else field.el.removeAttribute('data-pending');
    // SC6a R4: the formatting toolbar (decision 9) — none on a read-only field (locked, or no plan); above the editor.
    if(!note.locked&&!_clinRO&&!_aiPending) taWrap.appendChild(_tbEl=buildRichToolbar(field)); else field.onStateChange=null;
    field.attachTo(taWrap);
    if(field.loaded){
      // A re-render of the same note: the live editor (text, selection, undo history) is kept exactly as it is.
    } else {
      // Notes SC1: a failed load is NOT an empty note — the field stays disabled (no typing, no Save/Lock/Publish/
      // dictation) with the load error + Retry until a load succeeds. The editor bundle loads in the same step
      // (decision 25.14: its failure shows notes_rich_load_failed + Retry — there is no textarea fallback).
      const _draftDoc=_asDraftDoc(note.id); // F-5: read now, before this render re-binds the engine to the new field
      const _loadPaid=()=>{
        ta.disabled=true;ta.placeholder=T('vn2_loading'); _unloaded.add(note.id);
        const _draft=S._vaultDraftContent&&S._vaultDraftContent[note.id];
        const _got=(_draft!=null) ? Promise.resolve({ content:_draft, contentDoc:(_draftDoc&&docToPlain(_draftDoc)===_draft)?_draftDoc:null, fromDraft:true })
          : H()._fetchVaultNoteContent(note.id).then(r=>r.error?r:Object.assign({ contentDoc:r.content_doc||null },r));
        _got.then(async r=>{
          if(field.destroyed) return;
          if(r.error){
            if(await _bkRecoverOnOpen(note.id,null,r.error)){
              try{ await field.load({ content:field.value, contentDoc:field.doc }); /* SC6a: the recovered doc, not just its mirror */ }catch(e){ _loadFailed=true; ta.placeholder=''; _showErr('notes_rich_load_failed', _loadPaid); _syncTidyDisabled(); return; }
              _loadFailed=false; ta.disabled=false; ta.placeholder=''; _clearErr(); _clinDot.classList.toggle('vn2-dot-filled', !!ta.value.length); _syncTidyDisabled(); _contentLoaded(note.id); _asContentReady(); return; } // SC4: the device copy
            if(navigator.onLine===false && _draft==null){ _loadFailed=true; ta.placeholder=''; _clearErr(); _showOfflineCard(_loadPaid); _syncTidyDisabled(); return; } // QF5 = A
            _loadFailed=true; ta.placeholder=''; _showErr('notes_err_load', _loadPaid); _syncTidyDisabled(); return; }
          _hideOfflineCard();
          try{ await field.load({ content:r.content, contentDoc:r.contentDoc }); }
          catch(e){ _loadFailed=true; ta.placeholder=''; _showErr('notes_rich_load_failed', _loadPaid); _syncTidyDisabled(); return; }
          if(field.destroyed) return;
          _loadFailed=false; _clearErr(); ta.disabled=false; ta.placeholder='';
          _clinDot.classList.toggle('vn2-dot-filled', !!ta.value.length);_syncTidyDisabled(); _contentLoaded(note.id);
          if(!r.fromDraft){ _asLoaded(note.id,r); _asClinBase(note.id, field); } // F-19: the server's doc = the baseline (never an unsaved draft)
          _asContentReady(); if(!r.fromDraft) _bkRecoverOnOpen(note.id,r);});
      };
      _loadPaid();
    }
    _syncTidyDisabled(); // re-sync after a re-render that lands in the draft-exists branch (e.g. post-tidy / post-restore); harmless while loading (ta.disabled keeps Tidy off)
    ta.oninput=()=>{
      if(!S._vaultDraftContent)S._vaultDraftContent={};
      const _v=ta.value; // SC6a: the plain mirror (the draft map only needs "is there a draft")
      S._vaultDraftContent[note.id]=_v;
      _syncTidyDisabled();
      _asEdited(); // SC2: autosave (dictation dispatches 'input' too)
      _clinDot.classList.toggle('vn2-dot-filled', !!_v.length);
      _hideDictNotice(true); // SC6c: a clean-up notice goes on the next edit (the one-time hint stays its 12 s)
    };
    clinScroll.appendChild(taWrap);
  }
  // SC6c D2: the clean-up notice (decision 28.12): AI-edited + Undo for 6 s; kept / offline for 6 s. Hidden on the next edit.
  const _dictNotice = el('div',{class:'vn2-dict-notice',role:'status','aria-live':'polite'}); _dictNotice.hidden = true;
  let _dictNoticeT = null;
  let _dictNoticeKind = null;
  _hideDictNotice = (onEdit)=>{ if(onEdit && _dictNoticeKind==='hint') return; clearTimeout(_dictNoticeT); _dictNoticeT=null; _dictNoticeKind=null; _dictNotice.hidden = true; _dictNotice.replaceChildren(); }; // the one-time hint outlasts the dictated inserts
  _showDictNotice = (kind, o={})=>{
    _hideDictNotice();
    // Tidy guards T3: the same bar serves Tidy — 'tidied' (+ Undo), 'tidy_kept', 'tidy_too_long'
    const key = kind==='edited' ? 'notes_dict_ai_notice' : kind==='offline' ? 'notes_dict_offline' : kind==='hint' ? 'notes_dict_hint'
      : kind==='tidied' ? 'notes_tidy_notice' : kind==='tidy_kept' ? 'notes_tidy_kept' : kind==='tidy_too_long' ? 'notes_tidy_too_long'
      : kind==='production_kept' ? 'notes_dict_production_kept' : kind==='tidy_production_kept' ? 'notes_tidy_production_kept' : 'notes_dict_kept'; // decision 37.5
    _dictNoticeKind = kind;
    _dictNotice.className = 'vn2-dict-notice vn2-dict-notice-'+kind;
    _dictNotice.appendChild(el('span',{class:'vn2-dict-notice-msg'},[T(key)]));
    if(kind==='edited' && o.numfix) _dictNotice.appendChild(el('span',{class:'vn2-dict-notice-numfix'},[T('notes_dict_numfix',{ list:o.numfix })])); // decision 30
    if(kind==='edited' && o.wordfix) _dictNotice.appendChild(el('span',{class:'vn2-dict-notice-numfix vn2-dict-notice-wordfix'},[T('notes_dict_wordfix',{ list:o.wordfix })])); // decision 32
    // Tidy's lines: [key, vars] — numbers / words / headings added / formatting removed / lists flattened (decision 35)
    (o.lines||[]).forEach(([k,v])=>_dictNotice.appendChild(el('span',{class:'vn2-dict-notice-numfix vn2-dict-notice-line','data-key':k},[v?T(k,v):T(k)])));
    if((kind==='edited' || kind==='tidied') && o.undo){ const b=el('button',{type:'button',class:'vn2-dict-notice-undo'},[T('notes_tb_undo')]); b.onclick=()=>{ _hideDictNotice(); o.undo(); }; _dictNotice.appendChild(b); }
    _dictNotice.hidden = false;
    _dictNoticeT = setTimeout(()=>_hideDictNotice(), kind==='hint' ? 12000 : (o.lines && o.lines.length) ? 10000 : 6000);
  };
  clinPane.appendChild(_dictNotice);
  clinPane.appendChild(clinScroll);

  // ── PARENT PANE ──
  const parPane = el('div',{class:'vn2-pane vn2-pane-parent'});
  if(_isPhone) parPane.classList.add('vn2-pane-hidden'); // phone starts on Clinical
  const parInner = el('div',{class:'vn2-collapse-inner',style:{display:'flex',flexDirection:'column',minHeight:'0',flex:'1'}});

  const parHd = el('div',{class:'vn2-pane-hd'});
  const parLabel = el('div',{class:'vn2-pane-hd-label'});
  parLabel.appendChild(el('span',{'aria-hidden':'true'},['👁']));
  parLabel.appendChild(el('span',{},[T('vn2_parent_summary')]));
  parLabel.appendChild(el('span',{class:'vn2-badge vn2-badge-parents'},[T('vn2_visible_parents')]));
  parHd.appendChild(parLabel);
  const parHdActions = el('div',{class:'vn2-pane-actions'});
  if(_isDesktop){
    const collapseBtn = el('button',{class:'vn2-overflow','title':T('vn2_collapse'),'aria-label':T('vn2_collapse')},['⟩']);
    collapseBtn.onclick=()=>{ parPane.classList.add('vn2-collapsed'); };
    parHdActions.appendChild(collapseBtn);
  }
  parHd.appendChild(parHdActions);
  parInner.appendChild(parHd);

  // Toolbar: AI "Generate from clinical notes" — hidden on locked notes (N1 parity: AI lived
  // inside the !note.locked action row). Same handler/gate/behavior otherwise.
  let parToolbar=null; // F-53: a remote lock removes it in place
  if(!note.locked){
  parToolbar = el('div',{class:'vn2-pane-hd',style:{marginBottom:'8px'}});
  let aiSumBtn;
  const aiSumLbl = el('span',{style:{marginInlineStart:'6px'}},[T('vn2_generate')]);
  if(_aiOk){
    aiSumBtn=mkBtn('','btn-sm btn-secondary',async()=>{
      if(!ta.value||!ta.value.trim()){toast(T('vault_write_first'),'error');return;}
      aiSumBtn.disabled=true;aiSumLbl.textContent=T('vault_ai_generating');
      try{
        const _aiSess=await _supa.auth.getSession();const _aiTok=_aiSess?.data?.session?.access_token;
        if(!_aiTok){toast(T('vn2_session_expired'),'error');aiSumBtn.disabled=false;aiSumLbl.textContent=T('vn2_generate');return;}
        const resp=await fetch('/api/ai-summary',{method:'POST',headers:{'Content-Type':'application/json','Authorization':'Bearer '+_aiTok},body:JSON.stringify({notes:ta.value,childName:child?.name||_sp?.name||'the child',lang:H()._speechLang==='he-IL'?'he':'en'})});
        const result=await resp.json().catch(()=>({}));
        if(resp.status===403){
          H()._showSpecAiUpgradeModal('AI Parent Summary');   // QA-01: "No AI access" → upgrade/trial modal
        } else if(!resp.ok){
          toast(result.error||T('vault_could_not_generate'),'error');
        } else if(result.summary){
          // SC6a (decision 25.10): the summary box is updated IN PLACE — no re-render (the clinical editor and its history
          // stay). insertText over a full selection = one native undo step; the box's own input handler autosaves it.
          if(!sta.readOnly){
            sta.focus(); sta.select();
            let _ok=false; try{ _ok=document.execCommand && document.execCommand('insertText',false,result.summary); }catch(_){}
            if(!_ok || sta.value!==result.summary){ sta.value=result.summary; sta.dispatchEvent(new Event('input')); }
          }
          toast(T('vault_ai_generated'),'success');
        } else {toast(result.error||T('vault_could_not_generate'),'error');}
      }catch(e){console.error('AI summary:',e);toast(T('vault_could_not_reach_ai'),'error');}
      aiSumBtn.disabled=false;aiSumLbl.textContent=T('vn2_generate');
    });
  } else if(!_aiPending){ // F-53: no upgrade ghost while the plan isn't known yet
    aiSumBtn=mkBtn('','btn-sm btn-ghost',()=>H()._showSpecAiUpgradeModal('AI Parent Summary'));
  }
  if(aiSumBtn){ aiSumBtn.replaceChildren(el('span',{'aria-hidden':'true'},['✨']), aiSumLbl); parToolbar.appendChild(aiSumBtn); }
  parInner.appendChild(parToolbar);
  }

  // Summary textarea (fills height)
  const _sumLocked = !!note.published;
  const parScroll = el('div',{class:'vn2-scroll'});
  const sumTxt = _curSummary(); // SC1c: an emptied summary stays empty
  const sta = el('textarea',{class:'vn2-ta',dir:'auto'});
  sta.id='vault-summary-'+note.id;
  sta.value=sumTxt;
  sta.readOnly=_sumLocked;
  sta.placeholder=T('vault_summary_placeholder');
  if(!_sumLocked){
    sta.oninput=e=>{
      S.vaultSummary=e.target.value;
      const ns=DB.vaultNotes,i=ns.findIndex(n=>n.id===note.id);
      if(i>-1){ns[i].summary=e.target.value;DB.vaultNotes=ns;}
      _asEdited(); // SC2: autosave
      _parDot.classList.toggle('vn2-dot-filled', !!e.target.value.length);
    };
  }
  parScroll.appendChild(sta);
  parInner.appendChild(parScroll);

  // Footer: status (start) + Publish (end)
  const parFooter = el('div',{class:'vn2-pane-footer'});
  let statusText;
  if(note.published) statusText=T('vault_summary_sent');
  else if(note.pendingPublish) statusText=T('vault_pending_publish_toast');
  else statusText=T('vn2_not_sent');
  const _statusEl = el('div',{class:'vn2-footer-status'},[statusText]);
  parFooter.appendChild(_statusEl);
  if(!note.published&&!note.pendingPublish&&!_vaultRemoved){
    const pubBtn=mkBtn(T('vault_publish'),'btn-md btn-primary',()=>{
      if(_notReady()) return;
      if(_as && _as.conflict) return; // SC3: disabled until the conflict is resolved
      if(!_curSummary().trim()){toast(T('vault_write_summary_first'),'error');return;} // SC1c: emptied summary blocks Publish
      pubBtn.disabled=true;
      const _ctx2=notePatientCtx();
      const _isPrePar=_ctx2?.kind==='spec_patient';
      const _runPublish=async()=>{
        if(_notReady()){ pubBtn.disabled=false; return; }
        _clearErr();
        pubBtn.disabled=true;
        // Notes SC2 (A2): first save any pending/in-flight autosave; if that fails, stop (no publish, no notify).
        if(!(await notesFlush())){ pubBtn.disabled=false; _showErr('notes_err_publish', _runPublish); return; }
        _asPause();
        const _seq=_as?_as.editSeq:0;
        const _title=_as?_asTitleNow(_as,(DB.vaultNotes||[]).find(n=>n.id===note.id)||note):ti.value; // SC5
        const _content=ta.value, _clin=_clinFields(); // SC6a: both columns from the rich field; the free tier writes neither
        const _summary=_summaryForWrite(); // SC1c
        try{
          // SC3b: publishing a LOCKED note keeps it locked and never writes its clinical text (decision 4)
          const _lockedNow=!!((DB.vaultNotes.find(n=>n.id===note.id)||note).locked);
          const _publishFields=_isPrePar
            ?{is_draft:false,locked:_lockedNow,published:false,pending_publish:true}
            :{is_draft:false,locked:_lockedNow,published:true,pending_publish:false};
          // SC3: conditional on the note's version; a conflict → no publish write, no notification
          const _res=await _asStateWrite(_as,Object.assign(_lockedNow?{summary:_summary}:Object.assign({title:_title,summary:_summary},_clin),_publishFields),'publish note',
            (ts)=>_supa.from('vault_notes').upsert(Object.assign({id:note.id,specialist_id:specId,title:_title,summary:_summary,last_saved:ts,appointment_id:(_as&&_as.link)||null},_lockedNow?{}:_clin,_publishFields,H()._noteFkPayload(note)),{onConflict:'id'}));
          if(_res.r==='conflict'||_res.r==='deleted'){ _asConflict(_as,_res.r,_res.other); return; }
          // Notes SC1: no confirmed write → no notification, button back to its pre-publish state, Retry.
          if(_res.r!=='ok'){ _asResume(); pubBtn.disabled=false; _showErr('notes_err_publish', _runPublish); return; }
          H()._trackVaultNoteChange(note.id); // SC2: after the confirmed write
          _asMarkSaved(_seq,_res.ts);
          const ns=DB.vaultNotes,i=ns.findIndex(n=>n.id===note.id);
          if(i>-1){
            ns[i].title=_title;if(_clin.content!=null&&!_lockedNow)ns[i].content=_content;ns[i].summary=_summary;ns[i].isDraft=false;
            if(_isPrePar){ns[i].pendingPublish=true;ns[i].published=false;}
            else{ns[i].published=true;ns[i].pendingPublish=false;}
            DB.vaultNotes=ns;
          }
          if(!_isPrePar){
            const noteChild=DB.children.find(c=>c.id===note.childId)||(H().LS.get('children',[])).find(c=>c.id===note.childId);
            await H().notifyOtherParty('report',T('vn2_published_notif',{name:session.name||T('vn2_notif_specialist_fallback'),child:noteChild?.name||T('vn2_notif_child_fallback')}),note.childId,'session-summaries',null,null,T('notif_report_count',{n:'{n}',name:noteChild?.name||''}),H()._i18nBoth?.(l=>H().T_in(l,'vn2_published_notif',{name:session.name||H().T_in(l,'vn2_notif_specialist_fallback'),child:noteChild?.name||H().T_in(l,'vn2_notif_child_fallback')})),H()._nTw?.('notif_report_count',{n:'{n}',name:noteChild?.name||''}));
          }
          if(S._vaultDraftContent)delete S._vaultDraftContent[note.id];if(S._vaultDraftTitle)delete S._vaultDraftTitle[note.id];if(S._vaultTidyRaw)delete S._vaultTidyRaw[note.id];
          S.vaultHasUnsaved=false;ctl.onReflect();
          toast(_isPrePar?T('vault_pending_publish_toast'):T('vault_published'));
        }catch(e){console.error('publish note:',e);toast(T('vault_could_not_publish'),'error');pubBtn.disabled=false;}
      };
      if(_isPrePar){
        openModal(T('vault_pending_publish_title'),(mb,close)=>{
          mb.appendChild(H().p(T('vault_pending_publish_msg'),{marginBottom:'20px'}));
          const row=H().d('',[],{display:'flex',gap:'11px',justifyContent:'flex-end'});
          row.appendChild(mkBtn(T('vault_pending_publish_cancel'),'btn-md btn-ghost',()=>{pubBtn.disabled=false;close();}));
          row.appendChild(mkBtn(T('vault_pending_publish_confirm'),'btn-md btn-primary',()=>{close();_runPublish();}));
          mb.appendChild(row);
        },420);
      } else {
        openConfirm(T('vault_publish_confirm'),T('vault_publish_msg'),false,()=>_runPublish());
      }
    });
    parFooter.appendChild(pubBtn);
    _pubBtnRef=pubBtn;
  }
  parInner.appendChild(parFooter);
  parPane.appendChild(parInner);

  // Collapse rail (expand) — only shown when .vn2-collapsed (>=1024)
  const rail = el('div',{class:'vn2-rail','title':T('vn2_expand')});
  rail.appendChild(el('span',{'aria-hidden':'true'},['👁']));
  rail.appendChild(el('div',{class:'vn2-rail-label'},[T('vn2_parent_summary')]));
  rail.onclick=()=>{ parPane.classList.remove('vn2-collapsed'); };
  parPane.appendChild(rail);

  // ══════════════════ N2b — Past sessions (paid tier; F-19 QR-d B: also without a plan — it only reads) ══════════════════
  let pastRail=null, pastPane=null, segPast=null, peekWin=null;
  let _paintPast=()=>{}; // F-53: the list refresh repaints the past sessions in place (the sheet is never redrawn for it)
  if(_aiOk || !!H()._specAiReadOnly?.()){
    // Same cached rows the list uses — this specialist's own notes for this patient,
    // minus the open note, newest first. No new query.
    const _pastList=()=>{
      const _pastAll = _ctx?.kind==='spec_patient'
        ? DB.vaultNotes.filter(n=>n.specialistId===specId && n.specialistPatientId===_ctx.specialist_patient_id)
        : DB.vaultNotes.filter(n=>n.specialistId===specId && n.childId===_ctx?.child_id);
      return _pastAll.filter(n=>n.id!==note.id).sort((a,b)=>noteSortKey(b).localeCompare(noteSortKey(a))); // SC5: session date, newest first
    };
    let _pastNotes = _pastList();

    // Short date for rows/window (e.g. "Sep 4"); falls back to the raw value.
    const _shortDate=(ds)=>{ try{ const d=new Date(String(ds)+'T12:00:00'); if(isNaN(d)) return ds||''; return H()._locDate(d,{month:'short',day:'numeric'},'en-US'); }catch(e){ return ds||''; } };
    // A title is the auto default when it equals formatNoteTitle(creation); the creation ms is
    // embedded in the id ('v_<ms>'). Show the title line only for a CUSTOM (renamed) title.
    const _isDefaultTitle=isDefaultNoteTitle; // shared helper (rail hides title / list hides date)
    // Peek content: fetch on click via the bridge, cache in memory for the sheet's life.
    const _fillPeekBody=(id,node)=>{
      if(_peekCache.has(id)){ paragraphsInto(node,_peekCache.get(id)); return; }
      node.textContent=T('vn2_loading');
      H()._fetchVaultNoteContent(id).then(r=>{
        // Notes SC1: a failed load shows the error + Retry (never cached, never shown as an empty note)
        if(r.error){ const b=el('button',{class:'vn2-errbar-retry',type:'button'},[T('notes_retry')]); b.onclick=()=>_fillPeekBody(id,node);
          node.replaceChildren(el('span',{class:'vn2-errbar-msg'},[T('notes_err_load')]),' ',b); return; }
        _peekCache.set(id, r.content); paragraphsInto(node,r.content); });
    };
    // Rail / popover / phone-list row: short date + chip; title line only when custom.
    const _buildRow=(n,onClick)=>{
      const row=el('button',{class:'vn2-rail-row'});
      const top=el('div',{class:'vn2-rail-row-top'});
      top.appendChild(el('span',{class:'vn2-rail-date'},[el('bdi',{dir:'auto'},[noteDateLabel(n)])])); // SC5: from the data
      const ch=_computeChip(n,false,false);
      top.appendChild(el('span',{class:'vn2-rail-chip',style:{background:ch.bg,color:ch.fg}},[ch.label]));
      row.appendChild(top);
      if(!_isDefaultTitle(n)) row.appendChild(el('div',{class:'vn2-rail-title'},[H()._displayNoteTitle(n.title)]));
      row.onclick=onClick;
      return row;
    };

    // ── Floating past-session window (>=768): ONE at a time, draggable + resizable, clamped in the sheet ──
    const _rtl=!!(H()._isRtl && H()._isRtl());
    const peekWinEl=el('div',{class:'vn2-peek-win',tabindex:'-1'}); // focusable so Esc can target it
    const peekFrameEl=el('div',{class:'vn2-peek-frame'}); // F-63: the sheet scrolls as a whole, so the window sits in a viewport-tall sticky frame and every clamp below measures THIS frame (not the tall root)
    const _applyPeekPos=()=>{
      if(_peekPos){ peekWinEl.style.left=_peekPos.left+'px'; peekWinEl.style.top=_peekPos.top+'px';
        peekWinEl.style.right='auto'; peekWinEl.style.bottom='auto'; }
    };
    const _applyPeekSize=()=>{
      if(_peekSize){ peekWinEl.style.width=_peekSize.w+'px'; peekWinEl.style.height=_peekSize.h+'px'; }
    };
    // Resize from the bottom inline-end grip. Pins the window to physical left/top so growth goes
    // toward the grip's corner; RTL grows toward physical-left (recomputing left), LTR toward the right.
    const _attachResize=(grip)=>{
      let sx,sy,startW,startH,startLeft,startTop,rightFixed,resizing=false;
      grip.addEventListener('pointerdown',(e)=>{
        e.stopPropagation();
        const rr=peekFrameEl.getBoundingClientRect(), wr=peekWinEl.getBoundingClientRect();
        startW=wr.width; startH=wr.height; startLeft=wr.left-rr.left; startTop=wr.top-rr.top;
        rightFixed=startLeft+startW; sx=e.clientX; sy=e.clientY; resizing=true;
        _peekPos={left:startLeft, top:startTop};
        peekWinEl.style.left=startLeft+'px'; peekWinEl.style.top=startTop+'px';
        peekWinEl.style.right='auto'; peekWinEl.style.bottom='auto';
        try{ grip.setPointerCapture(e.pointerId); }catch(_){}
        e.preventDefault();
      });
      grip.addEventListener('pointermove',(e)=>{
        if(!resizing) return;
        // Clamp SIZE to the sheet inner (−32); the anchored edge slides inward when growth would
        // hit an edge, so the window enlarges toward the free space instead of jamming in the corner.
        const rr=peekFrameEl.getBoundingClientRect();
        const nw=Math.max(280, Math.min(startW+(_rtl?-1:1)*(e.clientX-sx), rr.width-32));
        const nh=Math.max(200, Math.min(startH+(e.clientY-sy), rr.height-32));
        const nl=_rtl ? Math.max(0, rightFixed-nw) : Math.min(startLeft, rr.width-nw);
        const nt=Math.min(startTop, rr.height-nh);
        _peekPos={left:nl, top:nt}; _peekSize={w:nw, h:nh};
        peekWinEl.style.left=nl+'px'; peekWinEl.style.top=nt+'px';
        peekWinEl.style.width=nw+'px'; peekWinEl.style.height=nh+'px';
      });
      const _end=(e)=>{ if(resizing){ resizing=false; try{ grip.releasePointerCapture(e.pointerId); }catch(_){}} };
      grip.addEventListener('pointerup',_end);
      grip.addEventListener('pointercancel',_end);
    };
    const _attachDrag=(handle)=>{
      let sx,sy,startLeft,startTop,dragging=false;
      handle.addEventListener('pointerdown',(e)=>{
        if(e.target.closest('.vn2-peek-close')) return; // Close isn't a drag
        const rr=peekFrameEl.getBoundingClientRect(), wr=peekWinEl.getBoundingClientRect();
        startLeft=wr.left-rr.left; startTop=wr.top-rr.top; sx=e.clientX; sy=e.clientY; dragging=true;
        try{ handle.setPointerCapture(e.pointerId); }catch(_){}
        e.preventDefault();
      });
      handle.addEventListener('pointermove',(e)=>{
        if(!dragging) return;
        const rr=peekFrameEl.getBoundingClientRect(), wr=peekWinEl.getBoundingClientRect();
        const nl=Math.max(0, Math.min(startLeft+(e.clientX-sx), rr.width-wr.width));
        const nt=Math.max(0, Math.min(startTop+(e.clientY-sy), rr.height-wr.height));
        _peekPos={left:nl, top:nt};
        peekWinEl.style.left=nl+'px'; peekWinEl.style.top=nt+'px';
        peekWinEl.style.right='auto'; peekWinEl.style.bottom='auto';
      });
      const _end=(e)=>{ if(dragging){ dragging=false; try{ handle.releasePointerCapture(e.pointerId); }catch(_){}} };
      handle.addEventListener('pointerup',_end);
      handle.addEventListener('pointercancel',_end);
    };
    const _renderPeekWindow=()=>{
      const n=_peekId && DB.vaultNotes.find(x=>x.id===_peekId);
      if(!n){ _peekId=null; peekWinEl.classList.remove('vn2-peek-open'); peekWinEl.replaceChildren(); return; }
      peekWinEl.replaceChildren();
      const hd=el('div',{class:'vn2-pw-hd'});
      hd.appendChild(el('span',{class:'vn2-peek-grip','aria-hidden':'true'},['⠿']));
      hd.appendChild(el('span',{class:'vn2-peek-date'},[el('bdi',{dir:'auto'},[noteDateLabel(n)])]));
      hd.appendChild(el('span',{class:'vn2-peek-ro'},[T('vn2_readonly')]));
      const acts=el('div',{class:'vn2-peek-actions'});
      const closeBtn=el('button',{class:'vn2-peek-close','title':T('vn2_close')},[T('vn2_close')]);
      closeBtn.onclick=(e)=>{ e.stopPropagation(); _peekId=null; _renderPeekWindow(); };
      acts.appendChild(closeBtn); hd.appendChild(acts);
      _attachDrag(hd);
      peekWinEl.appendChild(hd);
      // Body = ONE scroll area: clinical text fills it; parent summary is a collapsible section
      // at the bottom that scrolls with the content (never its own fixed region).
      const body=el('div',{class:'vn2-peek-body'});
      const clin=el('div',{class:'vn2-peek-clin'}); // F-55c: each paragraph carries its own dir=auto (paragraphsInto), the container none
      _fillPeekBody(n.id, clin);
      body.appendChild(clin);
      if(n.summary){
        body.appendChild(el('div',{class:'vn2-peek-divider'}));
        const tog=el('button',{class:'vn2-peek-sumtog'});
        tog.appendChild(el('span',{},[T('vn2_parent_summary')]));
        const chev=el('span',{class:'vn2-peek-chev'+(_peekSumOpen?' vn2-peek-chev-open':''),'aria-hidden':'true'},['▸']);
        tog.appendChild(chev);
        const sum=el('div',{class:'vn2-peek-sum'}); paragraphsInto(sum,n.summary);
        sum.hidden=!_peekSumOpen;
        tog.onclick=()=>{ _peekSumOpen=!_peekSumOpen; sum.hidden=!_peekSumOpen; chev.classList.toggle('vn2-peek-chev-open',_peekSumOpen); };
        body.appendChild(tog);
        body.appendChild(sum);
      }
      peekWinEl.appendChild(body);
      const grip=el('div',{class:'vn2-peek-resize','aria-hidden':'true'});
      _attachResize(grip);
      peekWinEl.appendChild(grip);
      _applyPeekPos(); _applyPeekSize();
      peekWinEl.classList.add('vn2-peek-open');
    };
    const _openPeekWindow=(id)=>{ if(id!==_peekId) _peekSumOpen=false; _peekId=id; _renderPeekWindow(); try{ peekWinEl.focus(); }catch(_){} }; // focus so Esc closes the window first; swaps content, keeps position/size
    peekFrameEl.appendChild(peekWinEl); peekWin=peekFrameEl; // the frame is appended to root after bodyEl (paints above the panes)
    _renderPeekWindow();       // restore a window that survived a refresh() rebuild

    // ── Desktop rail column ──
    pastRail=el('div',{class:'vn2-rail-past'});
    pastRail.appendChild(el('div',{class:'vn2-rail-past-hd'},[T('vn2_past_sessions')]));
    const railList=el('div',{class:'vn2-rail-list'});
    const _fillRail=()=>{
      railList.replaceChildren();
      if(!_pastNotes.length) railList.appendChild(el('div',{class:'vn2-rail-empty'},[T('vn2_no_past')]));
      else _pastNotes.forEach(n=> railList.appendChild(_buildRow(n, ()=>_openPeekWindow(n.id))));
    };
    _fillRail();
    pastRail.appendChild(railList);

    // ── Tablet popover trigger (768-1023): opens the same list anchored to the button ──
    const trigWrap=el('span',{style:{position:'relative',display:'inline-flex'}});
    const trigBtn=el('button',{class:'vn2-past-trigger','title':T('vn2_past_sessions')},[el('span',{'aria-hidden':'true'},['🗂']),el('span',{style:{marginInlineStart:'4px'}},[T('vn2_past_sessions')])]);
    let _pop=null;
    const _closePop=()=>{ if(_pop){ _pop.remove(); _pop=null; } };
    trigBtn.onclick=(e)=>{
      e.stopPropagation();
      if(_pop){ _closePop(); return; }
      _pop=el('div',{class:'vn2-popover'});
      if(!_pastNotes.length) _pop.appendChild(el('div',{class:'vn2-rail-empty'},[T('vn2_no_past')]));
      else _pastNotes.forEach(n=> _pop.appendChild(_buildRow(n, ()=>{ _openPeekWindow(n.id); _closePop(); })));
      trigWrap.appendChild(_pop);
      setTimeout(()=>document.addEventListener('click',_closePop,{once:true}),0);
    };
    trigWrap.appendChild(trigBtn);
    clinActions.appendChild(trigWrap);

    // ── Phone: 3rd segment pane (master list → read-only detail, back to list) ──
    pastPane=el('div',{class:'vn2-pane vn2-pane-past'});
    pastPane.classList.add('vn2-pane-hidden'); // phone default = clinical; >=768 CSS-hidden
    let _phoneDetail=null;
    const _renderPhonePast=(detailId)=>{
      _phoneDetail=detailId||null;
      pastPane.replaceChildren();
      if(detailId){
        const n=DB.vaultNotes.find(x=>x.id===detailId);
        if(!n){ _renderPhonePast(null); return; }
        const hd=el('div',{class:'vn2-pane-hd'});
        const back=el('button',{class:'vn2-back'},[el('span',{class:'vn2-chev','aria-hidden':'true'},['‹']),el('span',{},[T('vn2_past_sessions')])]);
        back.onclick=()=>_renderPhonePast(null);
        hd.appendChild(back); pastPane.appendChild(hd);
        const scroll=el('div',{class:'vn2-scroll'});
        const info=el('div',{class:'vn2-peek-hd',style:{marginBottom:'8px'}});
        info.appendChild(el('span',{class:'vn2-peek-date'},[el('bdi',{dir:'auto'},[noteDateLabel(n)])]));
        const ch=_computeChip(n,false,false);
        info.appendChild(el('span',{class:'vn2-rail-chip',style:{background:ch.bg,color:ch.fg,marginInlineStart:'0'}},[ch.label]));
        if(!_isDefaultTitle(n)) info.appendChild(el('span',{class:'vn2-peek-title'},[H()._displayNoteTitle(n.title)]));
        info.appendChild(el('span',{class:'vn2-peek-ro'},[T('vn2_readonly')]));
        scroll.appendChild(info);
        const body=el('div',{class:'vn2-peek-body',style:{maxHeight:'none',flex:'0 0 auto'}});
        _fillPeekBody(n.id, body); scroll.appendChild(body);
        if(n.summary){ scroll.appendChild(el('div',{class:'vn2-peek-divider'})); scroll.appendChild(el('div',{class:'vn2-peek-sum-label'},[T('vn2_parent_summary')])); { const _ps=el('div',{class:'vn2-peek-sum'}); paragraphsInto(_ps,n.summary); scroll.appendChild(_ps); } }
        pastPane.appendChild(scroll);
      } else {
        const hd=el('div',{class:'vn2-pane-hd'});
        hd.appendChild(el('div',{class:'vn2-pane-hd-label'},[el('span',{},[T('vn2_past_sessions')])]));
        pastPane.appendChild(hd);
        const list=el('div',{class:'vn2-scroll',style:{gap:'8px'}});
        if(!_pastNotes.length) list.appendChild(el('div',{class:'vn2-rail-empty'},[T('vn2_no_past')]));
        else _pastNotes.forEach(n=> list.appendChild(_buildRow(n, ()=>_renderPhonePast(n.id))));
        pastPane.appendChild(list);
      }
    };
    _renderPhonePast(null);
    _paintPast=()=>{
      _pastNotes=_pastList(); _fillRail();
      if(!_phoneDetail || !DB.vaultNotes.some(x=>x.id===_phoneDetail)) _renderPhonePast(null);
      if(_peekId && !DB.vaultNotes.some(x=>x.id===_peekId)) _renderPeekWindow();
    };

    // ── Phone: the 3rd switcher segment ──
    segPast=el('button',{class:'vn2-seg'},[el('span',{},[T('vn2_past_sessions')])]);
    switcher.appendChild(segPast);
  }

  // Segmented switcher wiring (phone): toggle pane visibility + active state
  const _showSeg=(which)=>{
    segClinical.classList.toggle('vn2-seg-active', which==='clinical');
    segParent.classList.toggle('vn2-seg-active', which==='parent');
    if(segPast) segPast.classList.toggle('vn2-seg-active', which==='past');
    clinPane.classList.toggle('vn2-pane-hidden', which!=='clinical');
    parPane.classList.toggle('vn2-pane-hidden', which!=='parent');
    if(pastPane) pastPane.classList.toggle('vn2-pane-hidden', which!=='past');
  };
  segClinical.onclick=()=>_showSeg('clinical');
  segParent.onclick=()=>_showSeg('parent');
  if(segPast) segPast.onclick=()=>_showSeg('past');

  bodyEl.appendChild(switcher);
  if(pastRail) bodyEl.appendChild(pastRail);   // >=1024: rail | clinical 3fr | parent 2fr
  bodyEl.appendChild(clinPane);
  bodyEl.appendChild(parPane);
  if(pastPane) bodyEl.appendChild(pastPane);   // phone 3rd segment (CSS-hidden >=768)
  root.appendChild(bodyEl);
  if(peekWin) root.appendChild(peekWin);       // floating past-session window (after body → paints above the panes)

  // Decision 7: the homework suggestion fires when LEAVING the note (index.js _requestClose), only with
  // clinical text, once per note (homework_prompt_shown) — never on autosave. Returns true when shown.
  const offerHw = ()=>{ if(!ta || !(ta.value||'').trim()) return false; const _fresh=DB.vaultNotes.find(n=>n.id===note.id); if(!_hwEligible||!_fresh||_fresh.homeworkPromptShown) return false; _maybeOfferHomework(); return true; };
  // SC3: conflict state → Lock and Publish disabled; the editor stays editable (the draft stays in memory).
  // C2: the amber banner (Keep my version / Use the other version / Copy my text), the red deleted-elsewhere
  // banner (read-only editor, Copy only), or the Restore chip after "Use the other version". Painted in place.
  const onConflictUI = (c)=>{
    const a=_as; const deleted=!!(c && c.kind==='deleted');
    if(lockBtn) lockBtn.disabled=!!c; if(_pubBtnRef) _pubBtnRef.disabled=!!c; if(delBtn) delBtn.disabled=deleted;
    [ti,ta,sta].forEach(x=>{ if(!x) return;
      if(deleted){ if(x.dataset.vn2Ro==null) x.dataset.vn2Ro=x.readOnly?'1':'0'; x.readOnly=true; }
      else if(x.dataset.vn2Ro!=null){ x.readOnly=x.dataset.vn2Ro==='1'; delete x.dataset.vn2Ro; } });
    _conflictSlot.replaceChildren();
    if(c){
      const box=el('div',{class:'vn2-conflict vn2-conflict-'+(deleted?'deleted':'conflict'),role:'alert','data-kind':c.kind});
      box.appendChild(el('span',{class:'vn2-conflict-ic','aria-hidden':'true'},[deleted?'🗑️':'⚠️']));
      const tx=el('div',{class:'vn2-conflict-tx'});
      tx.appendChild(el('div',{class:'vn2-conflict-t1'},[deleted?T('notes_deleted_title'):T('notes_conflict_title',{time:noteTimeHM(c.other)})])); // T() bidi-wraps {time}
      tx.appendChild(el('div',{class:'vn2-conflict-t2'},[deleted?T('notes_deleted_body'):T('notes_conflict_body')]));
      const acts=el('div',{class:'vn2-conflict-acts'});
      if(!deleted){
        const keep=el('button',{type:'button',class:'vn2-cbtn vn2-cbtn-p'},[T('notes_conflict_keep_mine')]);
        keep.onclick=()=>{ keep.disabled=true; Promise.resolve(_asKeepMine(a)).finally(()=>{ keep.disabled=false; }); };
        const use=el('button',{type:'button',class:'vn2-cbtn vn2-cbtn-s'},[T('notes_conflict_use_other')]);
        use.onclick=()=>{ use.disabled=true; Promise.resolve(_asUseOther(a)).finally(()=>{ use.disabled=false; }); };
        acts.appendChild(keep); acts.appendChild(use);
      }
      const copy=el('button',{type:'button',class:'vn2-cbtn '+(deleted?'vn2-cbtn-p':'vn2-cbtn-l')},[T('notes_conflict_copy_mine')]);
      copy.onclick=async()=>{ if(await _asCopyMine(a)){ copy.textContent=T('notes_conflict_copied'); setTimeout(()=>{ copy.textContent=T('notes_conflict_copy_mine'); },2000); } };
      acts.appendChild(copy);
      tx.appendChild(acts); box.appendChild(tx); _conflictSlot.appendChild(box);
    } else if(a && a.mine){
      const chip=el('div',{class:'vn2-restore'},[el('span',{},[T('notes_restore_hint')])]);
      const rb=el('button',{type:'button'},[el('span',{'aria-hidden':'true'},['↺ ']),T('notes_restore_mine')]);
      rb.onclick=()=>_asRestoreMine(a);
      chip.appendChild(rb); _conflictSlot.appendChild(chip);
    }
  };
  // F-53: a lock / publish / pending-publish change from another device, painted IN PLACE: the live editor keeps its
  // element, focus and caret (a redraw re-attaches it, which drops the iPad caret). Compared with what this editor SHOWS.
  // Only the one-way changes the product makes are painted; a flag turned off → false, and the caller re-renders.
  let _shown = { locked:!!note.locked, published:!!note.published, pending:!!note.pendingPublish };
  const paintFlags = ()=>{
    const cur=(DB.vaultNotes||[]).find(n=>n.id===note.id); if(!cur) return true;
    const nx={ locked:!!cur.locked, published:!!cur.published, pending:!!cur.pendingPublish };
    if(nx.locked===_shown.locked && nx.published===_shown.published && nx.pending===_shown.pending) return true;
    if((_shown.locked && !nx.locked) || (_shown.published && !nx.published) || (_shown.pending && !nx.pending && !nx.published)) return false;
    note.locked=nx.locked; note.published=nx.published; note.pendingPublish=nx.pending; // the handlers' closure copy
    const _drop=(b)=>{ if(!b) return; b.remove(); const i=_menuItems.indexOf(b); if(i>-1) _menuItems.splice(i,1); };
    if(nx.locked && !_shown.locked){
      _stopDict(); _closeMenu(); _hwEligible=false;
      ti.disabled=true; _drop(lockBtn); _drop(hwBtn);
      _clinEditActs.forEach(x=>x.remove()); _syncTidyDisabled=()=>{};
      if(_rb) _rb.remove(); if(_tbEl) _tbEl.remove();
      if(ta){ ta.onStateChange=null; ta.planRO=false; ta.el.removeAttribute('data-pending'); if(ta.dataset.vn2Ro!=null) ta.dataset.vn2Ro='1'; else ta.readOnly=true; }
      clinScroll.prepend(el('div',{class:'vn2-locked-banner'},[T('vault_locked_readonly')]));
      if(parToolbar) parToolbar.remove();
    }
    if(nx.locked || nx.published) _drop(delBtn);
    if(overflowBtn && !_menuItems.length){ _closeMenu(); overflowBtn.remove(); }
    if(nx.locked){ // the state chip (a locked note shows it; built on first lock)
      const _hc=_computeChip(cur, true), c=el('span',{class:'vn2-chip',style:{background:_hc.bg,color:_hc.fg}},[_hc.label]);
      if(chipEl) chipEl.replaceWith(c); else if(pillEl && pillEl.isConnected) pillEl.before(c); else titleWrap.appendChild(c);
      chipEl=c;
    }
    if(nx.locked && nx.published && pillEl) pillEl.remove();
    if(nx.published){ if(sta.dataset.vn2Ro!=null) sta.dataset.vn2Ro='1'; else sta.readOnly=true; sta.oninput=null; }
    if(nx.published || nx.pending){
      _statusEl.textContent=T(nx.published?'vault_summary_sent':'vault_pending_publish_toast');
      if(_pubBtnRef){ _pubBtnRef.remove(); _pubBtnRef=null; }
    }
    _shown=nx;
    if(_as && _as.id===note.id){ _asApplyFlags(_as, cur); _asMirror(_as); _asPaint(_as); }
    return true;
  };
  _asBind(note, { ti, ta, sta, pillEl, pillTxt, offerHw, onConflictUI, showErr:_showErr, reflect:()=>ctl.onReflect(),
    paintLink:_paintLink, paintFlags, paintPast:()=>_paintPast(), openNote:(id)=>{ if(ctl.openNote) ctl.openNote(id); } }); // Notes SC2: (re)bind the autosave engine + paint the pill
  // F-63c: a note built wide then narrowed (or the reverse) keeps a header and panes that match the CURRENT width. Move the buttons,
  // show the segment's pane, refresh the overlay's breakpoint class. Nothing is rebuilt, so the live editor, focus and caret stay.
  try{
    const mq=window.matchMedia('(max-width:767px)');
    const onBp=(e)=>{
      if(!root.isConnected){ if(mq.removeEventListener) mq.removeEventListener('change',onBp); else if(mq.removeListener) mq.removeListener(onBp); return; }
      const phone=!!(e&&e.matches!==undefined?e.matches:mq.matches);
      reslotHeaderActions({ phone, hactions, lockBtn, hwBtn, menuItems:_menuItems, getOverflow:()=>overflowBtn, closeMenu:_closeMenu, syncOverflow:_syncOverflow });
      if(phone) _showSeg(segParent.classList.contains('vn2-seg-active')?'parent':(segPast&&segPast.classList.contains('vn2-seg-active'))?'past':'clinical');
      if(ctl.onBreakpoint) ctl.onBreakpoint();
    };
    if(mq.addEventListener) mq.addEventListener('change',onBp); else if(mq.addListener) mq.addListener(onBp);
  }catch(e){ console.error('❌ notes width listener:',e); }
  return root;
}
